import { message } from 'antd';
import { produce } from 'immer';
import {
  defaultConfiguration,
  defaultItemValues,
  defaultMasks,
  ItemTypes,
  MASKS_TYPE,
  tableConfigDataAxisChanged,
  isConfigurableTable,
  defaultTableConfig,
  PLANNING_SCREENSHOT_TABLES,
  RECURSIVE_WORK_POSITION_PLANNING_IMAGE_FIELDS,
} from '../constants/constants';
import {
  calculateLeftPadding,
  calculateWidth,
  gridUnit,
} from '../constants/gridConfig';
import i18n from '../i18n';
import * as documentDatasApi from '../requests/documentDatas';
import * as foldersApi from '../requests/folders';
import * as proposalsApi from '../requests/proposals';
import * as templatesApi from '../requests/templates';
import * as userApi from '../requests/user';
import * as versionsApi from '../requests/versions';
import {
  initializeBoxContent,
  isATableBox,
  isAVariableBox,
  mutateBoxesOrderInfinite,
  reinitializeBoxWidthAndHeight,
} from '../utils/boxes';
import { computeColumnWidth } from '../utils/columns';
import { zoneSplitTableName } from '../shared/tables/zoneSplit';
import { getLegacyTableAlias, previewCatalogColumns } from '../shared/tables/tableDescriptors';
import { emptyUrl, getDocumentFromParams, updateUrl } from '../utils/browser';
import {
  buildUniqueContentData,
  formatResContent,
  formatTemplatePattern,
  formatVariables,
  syncStructure,
} from '../utils/dataSync';
import { dedupeSectionStyles, withUniqueColors } from '../utils/styles';
import { mutateResizeSummary, mutateTitlesIndexes } from '../utils/titles';
import {
  mutateExecuteParentTriggers,
  mutateRemoveDeletedSectionTriggers,
  mutateSearchAndLaunchTrigger,
} from '../utils/triggers';
import { getProposalSections, getTemplateSections } from '../requests/sections';
import {
  groupLinkedSectionZones,
  markLinkedSectionAnchors,
  restoreLinkedSectionAnchors,
  snapshotLinkedSectionAnchors,
} from '../utils/linkedSectionZones';
import {
  captureLinkedSectionAnswers,
  indexLinkedSectionQuestions,
  planAnswerRestore,
  selectAnswer,
  zonesAnchorIds,
} from '../utils/linkedSectionAnswers';
import { previewOpenBlockedBy, anyRemoteLockHolder } from '../collab/structureLocks';
import { computeDiff } from '../collab/diffEngine';
import { persistStructureDiff } from '../collab/persistence';
import { queryClient } from '../queryClient';
import { proposalHasMissingValues } from '../utils/formMissingValues';
import {
  fillEmptyAdminTablesBoxes,
  reapplyTableConfigLocally,
  tableConfigOnlyRenderingChanged,
  columnOriginLabel,
} from '../utils/table';
import { catalogFromRawTable } from '../shared/tables/tableColumns';
import {
  getRecursiveSectionFields,
  getRecursiveSectionRelationships,
  getRecursiveSectionData,
  getAvailableRootModels,
} from '../requests/recursiveSections';
import {
  expandContentForRows,
  getSectionRecursiveMeta,
  isIndexedRecursiveKey,
  isRecursiveSectionExpandedAtAnchor,
  shouldSkipRecursiveReloadExpansion,
} from '../utils/recursiveSections';
import {
  mutateInjectSection,
  mutateHandleLinkedSectionRecursive,
  rememberSectionName,
  mutateDeleteSection,
} from '../utils/sections';
import {
  mutateRemmoveSectionContainers,
  findTitleLevel1ContainerRange,
  filterChapterSeparationContainers,
  getContainerPrintIds,
} from '../utils/containers';
import {
  formatPagesPreview,
  mutateClosePreview,
  resolvePreviewStartContainerId,
} from '../utils/pagesPreview';
import { mutateInjectVariablesInDocument } from '../utils/variables';
import { fetchTableData, tableFetchArgs } from '../utils/table';
import {
  PENDING_SCREENSHOT,
  PLANNING_STATUS_MAX_REREADS,
  PLANNING_STATUS_REREAD_DELAY,
  countUnavailablePlanningImages,
  planningStatusEntries,
  planningStatusOfBox,
} from '../utils/planningScreenshots';
import { sortVariableOrderArray } from '../utils/variableOrderArray';
import { mutateAddVariableGroup } from '../utils/variableGroups';

export const defaultDocContent = {
  configuration: { ...defaultConfiguration },
  summaryConfiguration: {
    ...defaultItemValues.summary.content.configuration,
  },
  gridConfiguration: { ...defaultItemValues },
  // pages: [newPage()],
};

// Cache template fetches for the lifetime of the current document.
// Stores Promises (not resolved values) for in-flight deduplication.
// Cleared on document change via clearTemplateCache().
const _templateCache = new Map();

// Une section enregistrée avant l'ajout d'un réglage n'en a pas la clé
// (textStyles, checkboxStyles…), et l'injection lit sa configuration sans
// garde. Même complément par défaut qu'à l'ouverture d'un document (cf.
// applyTemplate), au seul point de passage commun à toutes les injections.
const withSyncedConfiguration = (template) =>
  template?.content
    ? {
        ...template,
        content: {
          ...template.content,
          configuration: syncStructure(
            template.content.configuration,
            defaultConfiguration
          ),
        },
      }
    : template;

// Filet de sécurité pour expandRemainingRecursiveAnchors : deux sections qui
// s'imbriquent mutuellement produiraient une chaîne d'ancres sans fin.
const RECURSIVE_ANCHOR_MAX_PASSES = 20;
// Un niveau de questions imbriquées par passe (cf. restoreLinkedSectionAnswers).
const ANSWER_RESTORE_MAX_PASSES = 20;

// La file sectionsToActivate est vidée en tâche de fond (middleware de
// store.js), sans être attendue par le chargement. Une ancre récursive
// imbriquée y attend d'être dépliée AVEC sa portée (sa ligne parente) : la
// déplier ici, sans portée, posait toutes les lignes du modèle sous elle (tous
// les articles sous une tenue qui n'en a aucun). On attend donc la fin du
// vidage en cours ; une ancre encore en file (vidage différé par la
// co-édition) reste à la file. Attente bornée : le middleware ne relâche pas
// son drapeau si une section lève, et le chargement ne doit pas rester figé.
const SECTIONS_ACTIVATION_MAX_WAIT_MS = 30000;
const waitForSectionsActivation = async (get) => {
  const deadline = Date.now() + SECTIONS_ACTIVATION_MAX_WAIT_MS;
  while (get().areSectionsActivating && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
};

// Mirror of backend RecursiveSections::Config.effective_model_key: `materiel` is the only
// model whose field schema depends on the agreement category. Under a security-style category
// it resolves to the `materiel_security` schema (different columns + title->name alias). Field
// definitions must be cached and looked up under this effective key so the palette, the indexed
// values and the API rows all reference the same field keys; otherwise the keys mismatch.
const SECURITY_FIELD_CATEGORIES = new Set(['security', 'accueil', 'other']);
const effectiveRecursiveModelKey = (modelName, category) =>
  modelName === 'materiel' && SECURITY_FIELD_CATEGORIES.has(String(category))
    ? 'materiel_security'
    : modelName;

export const apiActions = (set, get) => ({
  apiError: (error) => {
    emptyUrl();
    _templateCache.clear();
    set(
      produce((state) => {
        state.currentProposal = { id: null };
        state.currentDocument = getDocumentFromParams();
      }),
      false,
      `API error : ${error.message}...`
    );
  },
  startLoading: () => {
    set(
      produce((state) => {
        state.loadingData = true;
      }),
      false,
      'Set loading data true'
    );
  },
  stopLoading: () => {
    set(
      produce((state) => {
        state.loadingData = false;
      }),
      false,
      'Set loading data false'
    );
  },
  /**
   * Les métiers du user, et rien d'autre. Les entrées du webviewer
   * (webEditorApp, adminApp, webTemplateAdminApp) n'ont pas de document à
   * charger : `fetchDatas` ferait bien plus que nécessaire, et sans lui
   * `works_details` n'arrivait jamais — la porte refusait alors tout le monde.
   */
  fetchUserWorks: async () => {
    const connectedUser = await userApi.getUser();
    if (!connectedUser) return null;
    set(
      produce((state) => {
        state.user = { ...state.user, ...connectedUser.data };
      }),
      false,
      'Set connected user works'
    );
    return connectedUser.data;
  },
  fetchDatas: async () => {
    // Get User
    get().startLoading();
    const connectedUser = await userApi.getUser();
    if (connectedUser) {
      set(
        produce((state) => {
          state.user = { ...state.user, ...connectedUser.data };
        }),
        false,
        'Set Connected User'
      );
    } else {
      get().stopLoading();
      set(
        produce((state) => {
          state.isDocumentLoading = false;
        }),
        false,
        `isDocumentLoading false - auth error`
      );
      message.error("Problème d'authentification", 0);
      return;
    }
    const currentDocument = get().currentDocument;
    if (!currentDocument.id) {
      get().stopLoading();
      set(
        produce((state) => {
          state.isDocumentLoading = false;
        }),
        false,
        `isDocumentLoading false - no document id`
      );
      return;
    }
    // TID - Template : applyTemplate gère isDocumentLoading
    if (currentDocument.type === 'template') {
      await get().applyTemplate(currentDocument.id);
      get().stopLoading();
      return;
    }

    // Pour opportunity/offer : ne mettre isDocumentLoading à true que si on a un pid dans l'URL
    // Si on est sur la liste des documents (sans pid), on ne doit pas afficher le loader
    const currentProposal = get().currentProposal;
    const hasProposalToLoad = currentProposal?.id;

    // S'assurer que isDocumentLoading est false au départ si on n'a pas de proposal à charger
    if (!hasProposalToLoad) {
      set(
        produce((state) => {
          state.isDocumentLoading = false;
        }),
        false,
        `isDocumentLoading false - no proposal to load (list view)`
      );
    } else {
      set(
        produce((state) => {
          state.isDocumentLoading = true;
        }),
        false,
        `isDocumentLoading true - loading proposal ${currentProposal.id}`
      );
    }

    try {
      await get().fetchTables();
      await get().fetchDefaultVariablesApi();

      let res = await proposalsApi.fetchProposals(
        currentDocument,
        get().variables
      );
      set(
        produce((state) => {
          state.proposals = res;
        }),
        false,
        `Populated proposal list`
      );

      if (res) {
        if (hasProposalToLoad) {
          if (res.some((proposal) => proposal.id === currentProposal.id)) {
            const fetchedProposal = await proposalsApi.fetchProposal(
              { id: currentProposal.id, type: currentDocument.type },
              get().defaultVariablesApi
            );
            await get().applyProposal(
              fetchedProposal,
              currentDocument.type,
              fetchedProposal.sections
            );
            // applyProposal gère isDocumentLoading, donc pas besoin de le remettre à false ici
          } else {
            // Proposal n'existe plus dans la liste
            updateUrl({ pid: null });
            set(
              produce((state) => {
                state.currentProposal = { id: null };
                state.proposalList = true;
                state.isDocumentLoading = false;
              }),
              false,
              `Remove pid - proposal not found`
            );
          }
        } else {
          // Sur la liste des documents, on doit arrêter le loading
          get().stopLoading();
        }
        // Si on n'avait pas de proposal à charger (liste des documents), isDocumentLoading est déjà false
        // Pas besoin de le remettre à false
      } else {
        // Pas de résultats, s'assurer que isDocumentLoading est false
        get().stopLoading();
        set(
          produce((state) => {
            state.isDocumentLoading = false;
          }),
          false,
          `isDocumentLoading false - no proposals found`
        );
      }
    } catch (error) {
      // En cas d'erreur, s'assurer que isDocumentLoading est false
      get().stopLoading();
      set(
        produce((state) => {
          state.isDocumentLoading = false;
        }),
        false,
        `isDocumentLoading false - error: ${error.message}`
      );
      throw error;
    }
  },

  deleteProposalStore: (value) => {
    set(
      produce((state) => {
        let index = state.proposals.findIndex((obj) => obj.id === value);
        state.proposals.splice(index, 1);
      }),
      false,
      `Deleted Proposal of id ${value}`
    );
  },

  setLoaderPdfToImage: (value) => {
    set(
      produce((state) => {
        if (value === true) {
          state.boxVariableSelectedId = null;
        }
        state.loaderPdfToImage = value;
      }),
      false,
      `setLoaderPdfToImage to ${value}`
    );
  },

  deleteTemplateStore: (value) => {
    set(
      produce((state) => {
        let index = state.templates.findIndex((obj) => obj.id === value);
        state.templates.splice(index, 1);
      }),
      false,
      `Deleted Template of id ${value}`
    );
  },

  createProposalStore: (value) => {
    set(
      produce((state) => {
        state.proposals.push(value);
      }),
      false,
      `Added ${value.name} to proposals`
    );
  },

  renameProposalStore: (value) => {
    set(
      produce((state) => {
        let index = state.proposals.findIndex((obj) => obj.id === value.id);
        state.proposals[index].name = value.name;
      }),
      false,
      `Changed ${value.id}'s name to ${value.name}`
    );
  },

  updateTemplateInfosStore: (value) => {
    set(
      produce((state) => {
        let index;
        if (value.pattern !== 'section') {
          index = state.templates.findIndex((obj) => obj.id === value.id);
          state.templates[index].name = value.name;
          state.templates[index].document_type_ids = value.document_type_ids;
          state.templates[index].admin_only = value.admin_only;
          if (
            state.currentTemplate &&
            state.currentTemplate.id === state.templates[index].id
          ) {
            state.currentTemplate = {
              ...state.currentTemplate,
              ...state.templates[index],
            };
          }
        } else {
          index = state.sections.findIndex((obj) => obj.id === value.id);
          state.sections[index].name = value.name;
          state.sections[index].document_type_ids = value.document_type_ids;
          state.sections[index].admin_only = value.admin_only;
          if (
            state.currentTemplate &&
            state.currentTemplate.id === state.sections[index].id
          ) {
            state.currentTemplate = {
              ...state.currentTemplate,
              ...state.sections[index],
            };
          }
        }
      }),
      false,
      `Changed ${value.id}'s name to ${value.name}`
    );
  },

  duplicateTemplateStore: (value) => {
    set(
      produce((state) => {
        if (value.pattern !== 'section') {
          state.templates.push(value);
        } else {
          state.sections.push(value);
        }
      }),
      false,
      `Duplicated ${value}`
    );
  },

  setProposalFavoriteStore: (id, isFavorite) => {
    set(
      produce((state) => {
        const proposal = state.proposals.find((p) => p.id === id);
        if (proposal) proposal.is_favorite = isFavorite;
      }),
      false,
      `Set proposal ${id} favorite to ${isFavorite}`
    );
  },

  duplicateProposalStore: (value, document_type_ids) => {
    set(
      produce((state) => {
        state.proposals.push({ ...value, document_type_ids });
      }),
      false,
      `Duplicated ${value} to proposals`
    );
  },

  addVariablePathToBox: (id, value) => {
    set(
      produce((state) => {
        state.boxes.find((b) => b.id === id).variablePath = value;
      }),
      false,
      `Added variablePath ${value}to box ${id}`
    );
  },
  handleProposalList: (value) => {
    if (value === true) {
      set(
        produce((state) => {
          state.selectedBoxId = null;
        }),
        false,
        `Unselect Box`
      );
    }
    set(
      produce((state) => {
        state.copies = [];
        state.proposalList = value;
      }),
      false,
      `Changed proposalList to ${value}`
    );
  },
  handleMaskList: (value) => {
    if (value === true) {
      set(
        produce((state) => {
          state.selectedBoxId = null;
        }),
        false,
        `Unselect Box`
      );
    }
    set(
      produce((state) => {
        state.maskList = value;
      }),
      false,
      `Changed maskList to ${value}`
    );
  },

  /* VARIABLES */
  fetchDefaultVariablesApi: async () => {
    const { id, type } = get().currentDocument;
    if (!id) return;
    const res = await documentDatasApi.getVariables({ id, type });
    set(
      produce((state) => {
        state.defaultVariablesApi = res;
      }),
      false,
      'fetchDefaultVariablesApi'
    );
  },
  fetchTable: async (
    tableName,
    proposal = null,
    { split = false, tableConfig = null, force = false, throwOnError = false } = {}
  ) => {
    const currentDocument = proposal ? proposal : get().currentDocument;
    if (currentDocument.type === 'template' || !tableName) return;
    const res = await documentDatasApi.getTable(currentDocument, tableName, {
      split,
      tableConfig,
      force,
      throwOnError,
    });
    const statusEntries = planningStatusEntries({
      documentId: currentDocument.id,
      tableName,
      tableConfig,
      table: res,
    });
    if (statusEntries.length) {
      set(
        produce((state) => {
          statusEntries.forEach(([key, value]) => {
            state.planningScreenshotStatus[key] = value;
          });
        }),
        false,
        'Planning screenshot status'
      );
    }
    return res;
  },
  // Applique une nouvelle config (data + présentation) sur une box tableau
  // paramétrable. En mode template on ne persiste que la config (pas de données :
  // la matérialisation appliquera la config à l'application du template). En mode
  // proposition on refetch pour refléter les axes donnée (mode/consolidation/variant)
  // et réappliquer la présentation (colonnes) sur le jeu complet renvoyé.
  // Mémorise la réponse brute du backend pour une box tableau. Toute voie qui
  // fetch de vraies données (Drawer axe donnée, bouton "mettre à jour les
  // données") doit l'appeler -> le cache reste frais pour les réapplications de
  // présentation locales.
  cacheTableRaw: (boxId, table) => {
    if (!table) return;
    set(
      produce((state) => {
        state.tableRawCache[boxId] = table;
      }),
      false,
      'Cache raw table response'
    );
  },
  // Renommage d'une colonne depuis l'EN-TÊTE du canvas (double-clic) pour un
  // tableau variable : écrit dans le customLabel de la config (même valeur
  // que le Drawer) au lieu de column.title -> une seule source de vérité, canvas
  // et Drawer synchro. Le texte est du plein texte (le formatage riche d'en-tête
  // n'a pas de sens ici : il serait écrasé au prochain reapply de présentation).
  // Retourne false si l'appelant doit aussi renommer la colonne lui-même
  // (column.title) : libellé d'origine introuvable, ou tableau historique (cf.
  // persistCanvasTableConfig).
  setTableColumnCustomLabel: (boxId, columnKey, text) => {
    const box = get().boxes.find((b) => b.id === boxId);
    if (!box) return false;
    const originalLabel = get().tableColumnOriginLabel(box, columnKey);
    if (!originalLabel) return false;

    const current = box.tableConfig || { columns: [] };
    const columns = Array.isArray(current.columns) ? [...current.columns] : [];
    const trimmed = (text || '').trim();
    const customLabel = trimmed.length ? text : undefined;
    const idx = columns.findIndex((c) => c.label === originalLabel);
    // Titre inchangé (simple perte de focus de l'en-tête) : rien à écrire. Sinon
    // applyTableConfig relit la table et rend une colonne supprimée entre-temps.
    if (customLabel === (idx >= 0 ? columns[idx].customLabel : undefined) ||
        (idx < 0 && customLabel === originalLabel)) {
      return true;
    }
    if (idx >= 0) {
      columns[idx] = { ...columns[idx], customLabel };
    } else {
      columns.push({ label: originalLabel, customLabel });
    }
    return get().persistCanvasTableConfig(boxId, { ...current, columns });
  },
  // Alignement d'une colonne choisi au canvas (`field` : align | verticalAlign |
  // headerAlign, celui du seul titre)
  // pour un tableau lu de l'API : mémorisé dans la config, sinon la relecture
  // suivante (rafraîchir, réglage du Drawer) rend l'alignement de l'API.
  setTableColumnAlign: (boxId, columnKey, field, value) => {
    const box = get().boxes.find((b) => b.id === boxId);
    if (!box?.tableName) return;
    const originalLabel = get().tableColumnOriginLabel(box, columnKey);
    if (!originalLabel) return;

    const current = box.tableConfig || { columns: [] };
    const columns = Array.isArray(current.columns) ? [...current.columns] : [];
    const idx = columns.findIndex((c) => c.label === originalLabel);
    // Aligner la colonne entière aligne aussi son titre.
    const patch = field === 'align' ? { align: value, headerAlign: undefined } : { [field]: value };
    if (idx >= 0) {
      columns[idx] = { ...columns[idx], ...patch };
    } else {
      columns.push({ label: originalLabel, ...patch });
    }
    get().persistCanvasTableConfig(boxId, { ...current, columns });
  },
  // Config écrite depuis le canvas (renommage, largeur). Tableau paramétrable :
  // applyTableConfig réapplique la présentation en local et persiste via le
  // diff de structure. Tableau historique : le canvas a déjà fait le changement
  // à l'écran ; on ne fait que le mémoriser pour qu'il survive au prochain
  // rafraîchissement. Le réappliquer relirait la table (pas de cache au premier
  // geste) et effacerait les lignes ajoutées à la main. Retourne true si la
  // présentation a été réappliquée.
  persistCanvasTableConfig: (boxId, nextTableConfig) => {
    const box = get().boxes.find((b) => b.id === boxId);
    if (box && isConfigurableTable(box.tableName)) {
      get().applyTableConfig(boxId, nextTableConfig);
      return true;
    }
    get().updateBox(boxId, (b) => {
      b.tableConfig = nextTableConfig;
    });
    return false;
  },
  // Libellé d'ORIGINE d'une colonne (cf. columnOriginLabel), avec la réponse
  // brute en cache de la box.
  tableColumnOriginLabel: (box, columnKey) =>
    columnOriginLabel(box, columnKey, get().tableRawCache?.[box.id]),
  // Largeur de colonne modifiée depuis le CANVAS (drag du bord, input %) pour un
  // tableau variable : convertit les px en % et écrit dans tableConfig.columns
  // (même valeur que le Drawer) -> canvas et Drawer synchro, survit au reapply.
  // pxWidthByKey = { [col.key]: px } pour les colonnes visibles. Retourne true si
  // pris en charge, false sinon -> l'appelant garde son comportement px habituel.
  setTableColumnWidthsFromCanvas: (boxId, pxWidthByKey = {}) => {
    const box = get().boxes.find((b) => b.id === boxId);
    if (!box || !box.tableName) return false;
    const labels = (box.content?.columns || []).map((c) => get().tableColumnOriginLabel(box, c.key));
    // Une colonne sans libellé d'origine sûr : on garde les largeurs en px.
    if (labels.some((label) => !label)) return false;
    const cols = box.content?.columns || [];
    const total = cols.reduce(
      (sum, c) => sum + (pxWidthByKey[c.key] ?? c.width ?? 0),
      0
    );
    if (total <= 0) return true;

    const current = box.tableConfig || { columns: [] };
    const overrides = Array.isArray(current.columns) ? [...current.columns] : [];
    const indexByLabel = new Map(overrides.map((o, i) => [o.label, i]));
    cols.forEach((c, colIdx) => {
      const label = labels[colIdx];
      const px = pxWidthByKey[c.key] ?? c.width ?? 0;
      const pct = Math.max(1, Math.round((px / total) * 100));
      const i = indexByLabel.get(label);
      if (i != null) {
        overrides[i] = { ...overrides[i], width: pct };
      } else {
        overrides.push({ label, width: pct });
        indexByLabel.set(label, overrides.length - 1);
      }
    });
    get().persistCanvasTableConfig(boxId, { ...current, columns: overrides });
    return true;
  },
  // Suppression de colonnes depuis le CANVAS (croix) pour un tableau variable :
  // la config les masque (`hidden`, comme le Drawer). Sans cela, la prochaine
  // relecture ou réapplication de la présentation (rafraîchir, renommage d'un
  // en-tête, réglage du Drawer) rendrait la colonne supprimée. À appeler AVANT
  // de retirer les colonnes de la box : leur libellé d'origine se lit dessus. Ne
  // retire pas la colonne elle-même : c'est le rôle de l'appelant.
  // Retourne false sans rien écrire si une colonne n'a pas de libellé d'origine
  // sûr -> l'appelant garde sa suppression locale seule.
  hideTableColumnsFromCanvas: (boxId, columnKeys = []) => {
    const box = get().boxes.find((b) => b.id === boxId);
    if (!box?.tableName || columnKeys.length === 0) return false;
    const labels = columnKeys.map((key) => get().tableColumnOriginLabel(box, key));
    if (labels.some((label) => !label)) return false;

    const current = box.tableConfig || { columns: [] };
    const overrides = Array.isArray(current.columns) ? [...current.columns] : [];
    labels.forEach((label) => {
      const i = overrides.findIndex((o) => o.label === label);
      if (i >= 0) {
        overrides[i] = { ...overrides[i], hidden: true };
      } else {
        overrides.push({ label, hidden: true });
      }
    });
    // Config seule, sans applyTableConfig : la croix a déjà retiré la colonne à
    // l'écran, et rematérialiser les lignes effacerait celles ajoutées à la main
    // et leurs fusions. Le masquage sert aux relectures suivantes.
    get().updateBox(boxId, (b) => {
      b.tableConfig = { ...current, columns: overrides };
    });
    return true;
  },
  // Pose `tableName` dans une box tableau. Découpé (split), la 1re tranche
  // remplit la box et chaque tranche suivante devient une box ajoutée à la suite
  // dans le même container (un tableau par zone).
  applyTableChange: async (
    box,
    tableName,
    proposal,
    { split = false, tableConfig, onRawTable } = {}
  ) => {
    const {
      updateBox,
      fetchTable,
      addContainers,
      gridConfiguration,
      configuration,
      landscape,
      masks,
      containers,
    } = get();
    const config =
      tableConfig ??
      (isConfigurableTable(tableName) ? defaultTableConfig(tableName) : undefined);
    const nextBoxContents = await fetchTableData({
      fetchStoreFunc: fetchTable,
      updateBoxFunc: updateBox,
      box: { ...box, tableName, proposal, tableConfig: config },
      split,
      onRawTable,
    });
    const marginRight = configuration.margins.right * gridUnit;
    const marginLeft = configuration.margins.left * gridUnit;
    const header = masks.find(({ id }) => id === MASKS_TYPE.HEADER.id);
    const footer = masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id);
    const boxContainerIdx = containers.findIndex((c) =>
      c.columnsIds.includes(box.columnId)
    );
    for (const nextBoxContent of nextBoxContents.reverse()) {
      const { columns, data, columnsDisposition } =
        buildUniqueContentData(nextBoxContent);
      let newBox = initializeBoxContent({
        box: {
          type: ItemTypes.TABLE,
          ...gridConfiguration[ItemTypes.TABLE],
          copied: false,
        },
        configuration,
      });
      newBox = reinitializeBoxWidthAndHeight({
        box: newBox,
        columnWidth: computeColumnWidth({ landscape, marginRight, marginLeft }),
        onCreate: true,
        header,
        footer,
      });
      // gridConfiguration template may be Immer-frozen; replace content instead of mutating
      newBox = {
        ...newBox,
        hideField: nextBoxContent.hideField,
        hideInForm: nextBoxContent.hideInForm,
        content: {
          ...newBox.content,
          columns,
          columnsDisposition,
          data,
          identifiers: nextBoxContent.identifiers,
          shouldSplit: true,
        },
        tableName,
        proposal,
        tableConfig: config,
      };
      addContainers({
        index: boxContainerIdx,
        drawMode: false,
        box: newBox,
        shouldSelect: false,
      });
    }
  },
  // Bascule « un tableau par zone » d'une box paramétrable depuis le Drawer.
  // Template : on ne retient que le choix, la matérialisation fera le découpage.
  // Proposition : on recharge la box ; activer le découpage ajoute une box par
  // zone supplémentaire, le désactiver ne touche que cette box (les box déjà
  // créées pour les autres zones restent, à supprimer à la main).
  setTableSplit: async (boxId, split) => {
    const { updateBox, currentDocument, cacheTableRaw } = get();
    const box = get().boxes.find((b) => b.id === boxId);
    if (!box?.tableName) return;
    if (currentDocument.type === 'template') {
      updateBox(boxId, (b) => {
        if (!b.content) b.content = {};
        b.content.shouldSplit = split;
        // Un identifiant de zone n'a de sens que sur une box découpée.
        if (!split) delete b.content.identifiers;
      });
      return;
    }
    await get().applyTableChange(box, box.tableName, box.proposal, {
      split,
      tableConfig: box.tableConfig,
      onRawTable: (table) => cacheTableRaw(boxId, table),
    });
  },
  applyTableConfig: async (boxId, nextTableConfig) => {
    const { updateBox, fetchTable, currentDocument, tableRawCache } = get();
    const prevBox = get().boxes.find((b) => b.id === boxId);
    const prevConfig = prevBox?.tableConfig;
    updateBox(boxId, (box) => {
      box.tableConfig = nextTableConfig;
    });
    if (currentDocument.type === 'template') return;
    // Seuls les réglages de fusion ou le style des lignes ont changé : le rendu
    // les lit sur la box, rien à relire. Rematérialiser effacerait les lignes
    // ajoutées à la main et leurs fusions.
    if (tableConfigOnlyRenderingChanged(prevConfig, nextTableConfig, prevBox?.tableName)) return;
    const box = get().boxes.find((b) => b.id === boxId);
    if (!box) return;

    const dataAxisChanged = tableConfigDataAxisChanged(
      box.tableName,
      prevConfig,
      nextTableConfig
    );
    const cachedTable = tableRawCache?.[boxId];

    // Présentation seule (colonnes) + données brutes en cache -> on réapplique en
    // local, sans requête backend : renommer/masquer/réordonner/redimensionner une
    // colonne ne change pas les rows. Seuls les axes donnée (ou l'absence de cache)
    // déclenchent un refetch.
    if (!dataAxisChanged && cachedTable) {
      reapplyTableConfigLocally({
        table: cachedTable,
        box: { ...box, tableConfig: nextTableConfig },
        tableConfig: nextTableConfig,
        updateBoxFunc: updateBox,
        split: box.content?.shouldSplit,
      });
      return;
    }

    await fetchTableData({
      fetchStoreFunc: fetchTable,
      updateBoxFunc: updateBox,
      box: { ...box, tableConfig: nextTableConfig },
      split: box.content?.shouldSplit,
      refresh: true,
      // Mémorise la réponse brute -> les éditions de présentation suivantes sur
      // cette box deviennent locales (pas de requête).
      onRawTable: (table) => get().cacheTableRaw(boxId, table),
    });
  },
  // Catalogue de colonnes du Drawer pour n'importe quel tableau variable. Un
  // tableau paramétrable a son catalogue backend ; un historique n'en a pas :
  // ses colonnes sont les clés de sa réponse brute, gardée en cache pour les
  // réapplications de présentation qui suivront. En modèle, pas de données :
  // catalogue vide.
  fetchTableCatalog: async (box, tableConfig = null) => {
    if (isConfigurableTable(box.tableName)) {
      return get().fetchTableSchema(box.tableName, tableConfig, box.proposal);
    }
    const scopedRowId = box.content?.recursiveRowId ?? null;
    let raw = get().tableRawCache?.[box.id];
    if (!raw) {
      const zoneSplit = !!box.content?.shouldSplit && scopedRowId == null;
      raw = await get().fetchTable(
        zoneSplit ? zoneSplitTableName(box.tableName) : box.tableName,
        box.proposal,
        {
          split: !!box.content?.shouldSplit || scopedRowId != null,
          tableConfig,
        }
      );
      // Box de section récursive : sa slice dépend de la boucle, la
      // réapplication locale ne sait pas la retrouver -> pas de cache.
      if (raw && scopedRowId == null) get().cacheTableRaw(box.id, raw);
    }
    const identifiers = scopedRowId != null ? [String(scopedRowId)] : box.content?.identifiers;
    const columns = catalogFromRawTable(raw, identifiers);
    if (columns.length > 0) return { columns };
    // Sans ligne (modèle), l'API ne dit pas ses colonnes : un ancien nom prend
    // celles de son tableau paramétrable, un historique ses colonnes d'exemple.
    const alias = getLegacyTableAlias(box.tableName);
    if (alias) {
      return get().fetchTableSchema(alias.tableName, { ...tableConfig, ...alias.tableConfig }, box.proposal);
    }
    return {
      columns: previewCatalogColumns(box.tableName, tableConfig, { split: !!box.content?.shouldSplit }),
    };
  },
  fetchTableSchema: async (tableName, tableConfig = null, proposal = null) => {
    if (!tableName) return null;
    // Comme fetchTable : une box tableau dans une offre référence une opportunity
    // (box.proposal) -> le catalogue se lit sur ce document, pas sur l'offre.
    const doc = proposal || get().currentDocument;
    return documentDatasApi.getTableSchema(doc, tableName, {
      tableConfig,
    });
  },
  // Appelé sur réception du broadcast WS `planning_screenshots_ready` : re-fetch
  // les box tableaux planning-screenshot pour afficher les images fraîchement
  // générées (1er load ou bouton "Mettre à jour"). Sans force => simple lecture,
  // pas de nouvelle génération (anti-boucle).
  refreshPlanningScreenshotTables: async () => {
    const { boxes, fetchTable, updateBox } = get();
    const planningBoxes = boxes.filter(
      (b) =>
        isATableBox(b.type) &&
        b.tableName &&
        PLANNING_SCREENSHOT_TABLES.includes(b.tableName.toUpperCase())
    );
    if (planningBoxes.length === 0) return;
    await Promise.all(
      planningBoxes.map((box) =>
        fetchTableData({
          fetchStoreFunc: fetchTable,
          updateBoxFunc: updateBox,
          box: { ...box },
          split: box.content?.shouldSplit,
          recursiveRowId: box.content?.recursiveRowId,
          refresh: true,
          // Pas de `force` : ce re-fetch suit le broadcast WS, il vient lire
          // l'image fraîche. La forcer relancerait une capture, qui rebroadcast,
          // qui re-fetch — la boucle que ce chemin doit justement éviter.
        })
      )
    );
  },
  // Image planning restée vide (capture en cours ou en échec), à l'ouverture du
  // document ou pendant la génération lancée par un autre utilisateur (seul le
  // demandeur reçoit le broadcast WS). On relit l'état sans toucher aux box ;
  // une box n'est réécrite que si ses images sont prêtes, et pas en présence
  // d'un co-éditeur : la réécriture remplace lignes et fusions, et elle serait
  // diffusée et sauvegardée par chacun. Tant qu'un état reste « en cours », on
  // relit plus tard, un nombre borné de fois.
  refreshIncompletePlanningScreenshots: async ({ attempt = 0 } = {}) => {
    const { currentDocument, fetchTable, updateBox } = get();
    if (!currentDocument?.id || currentDocument.type === 'template') return;
    const incomplete = get().boxes.filter((b) => countUnavailablePlanningImages([b]) > 0);
    if (incomplete.length === 0) return;
    const tables = incomplete.filter((b) => isATableBox(b.type));
    const images = incomplete.filter((b) => !isATableBox(b.type));

    await Promise.all(
      tables.map((box) => {
        const { fetchName, effectiveSplit, fetchTableConfig } = tableFetchArgs(box, {
          split: box.content?.shouldSplit,
        });
        return fetchTable(fetchName, box.proposal, {
          split: effectiveSplit,
          tableConfig: fetchTableConfig,
        }).catch(() => null);
      })
    );
    const s = get();
    const alone = !(s.collabConnected && s.presence.some((u) => u.id !== s.user?.id));
    const statusOf = (box) =>
      planningStatusOfBox(get().planningScreenshotStatus, box, currentDocument.id);
    if (alone) {
      await Promise.all(
        tables
          .filter((box) => statusOf(box) === 'ready')
          .map((box) =>
            fetchTableData({
              fetchStoreFunc: fetchTable,
              updateBoxFunc: updateBox,
              box: { ...box },
              split: box.content?.shouldSplit,
              recursiveRowId: box.content?.recursiveRowId,
              refresh: true,
            })
          )
      );
      if (images.length) await get().refreshRecursivePlanningImages();
    }

    const stillPending =
      tables.some((box) => statusOf(box) === 'pending') ||
      get().boxes.some((b) => b.content?.src === PENDING_SCREENSHOT);
    if (stillPending && attempt < PLANNING_STATUS_MAX_REREADS) {
      setTimeout(() => {
        if (get().currentDocument?.id !== currentDocument.id) return;
        get().refreshIncompletePlanningScreenshots({ attempt: attempt + 1 });
      }, PLANNING_STATUS_REREAD_DELAY);
    }
  },
  /**
   * Proposal api
   */
  saveAndGeneratePdf: async (
    type,
    pagesPreviewExcludedIdx = null,
    startContainerId = null
  ) => {
    let pagesPreviewExcluded = [];
    if (typeof pagesPreviewExcludedIdx === 'number') {
      pagesPreviewExcluded = get().pagesPreview.filter(
        (_, idx) => idx < pagesPreviewExcludedIdx
      );
    }
    const {
      boxes,
      pages,
      masks,
      configuration,
      summaryConfiguration,
      gridConfiguration,
      currentDocument,
      currentTemplate,
      currentProposal,
      containers,
      columns,
      rowContainers,
      proposals,
      landscape,
      variables,
      templates,
      sections,
      variableGroups,
      variableOrderArray,
      triggers,
      tmp,
    } = get();

    if (!currentDocument.id) return;

    let containersIncluded = containers.filter((container) => {
      // Un container aplati par le service PDF (lignes principales promues en
      // containers) n'apparaît dans l'aperçu que sous l'id de ses lignes : sans
      // cette équivalence, il échapperait au filtre et serait rendu deux fois.
      const printIds = getContainerPrintIds(container, columns);
      return !pagesPreviewExcluded.some((pagePreview) =>
        pagePreview.printedContainers.some((pContainer) =>
          printIds.includes(pContainer.id)
        )
      );
    });

    let boxesIncluded = boxes;
    let columnsIncluded = columns;
    let resolvedStartContainerId = null;

    if (type === 'preview') {
      // Filter out hidden chapter separation containers before computing the
      // title1 range, so duplicate title1 entries don't corrupt the boundary.
      const displayChapterSeparation =
        configuration?.displayChapterSeparation ?? true;
      const visibleContainersForRange = filterChapterSeparationContainers({
        containers,
        boxes,
        columns,
        masks,
        displayChapterSeparation,
      });
      // Resolve startContainerId: if it points to a now-hidden container
      // (e.g. after toggling displayChapterSeparation), fall back to its
      // linked counterpart so the preview range stays correct.
      const visibleContainerIds = new Set(visibleContainersForRange.map((c) => c.id));
      resolvedStartContainerId = resolvePreviewStartContainerId(get(), startContainerId);
      if (resolvedStartContainerId && !visibleContainerIds.has(resolvedStartContainerId)) {
        const hiddenContainer = containers.find((c) => c.id === resolvedStartContainerId);
        if (hiddenContainer?.linkedContainerId && visibleContainerIds.has(hiddenContainer.linkedContainerId)) {
          resolvedStartContainerId = hiddenContainer.linkedContainerId;
        }
      }
      // Collab : aperçu refusé si un co-éditeur édite la section (box/variable)
      // ou en regarde déjà l'aperçu. Le toast réutilise la clé 'pdfKey' →
      // remplace le spinner de chargement (pas d'aperçu ouvert).
      const previewBlock = previewOpenBlockedBy(
        { ...get(), containers: visibleContainersForRange },
        resolvedStartContainerId
      );
      if (previewBlock) {
        const { holder, reason } = previewBlock;
        const content =
          reason === 'duplicate'
            ? `Aperçu déjà ouvert par ${holder.name}`
            : reason === 'variable'
            ? `Section en cours d'édition (variable) par ${holder.name}`
            : `Section en cours d'édition par ${holder.name}`;
        message.warning({ content, key: 'pdfKey' });
        return;
      }
      const {
        containersIncluded: computedContainersIncluded,
        boxesIncluded: computedBoxesIncluded,
        columnsIncluded: computedColumnsIncluded,
      } = findTitleLevel1ContainerRange({
        containers: visibleContainersForRange,
        boxes,
        columns,
        startContainerId: resolvedStartContainerId,
      });

      if (computedContainersIncluded?.length) {
        const titleLevel1ContainerIds = new Set(
          containersIncluded.map((container) => container.id)
        );
        containersIncluded = computedContainersIncluded.filter((container) =>
          titleLevel1ContainerIds.has(container.id)
        );
        boxesIncluded = computedBoxesIncluded;
        columnsIncluded = computedColumnsIncluded;
      }
    }

    // Filigrane réglé sur « valeurs manquantes » : figé au moment de l'aperçu
    // ou de l'export. En modèle, il reste affiché (undefined).
    const formIncomplete =
      currentDocument.type === 'template' ||
      configuration.watermark?.display !== 'missingValues'
        ? undefined
        : proposalHasMissingValues(
            get(),
            queryClient.getQueryData([
              'Sections',
              { documentType: currentDocument.type, documentId: currentProposal.id },
            ])
          );

    set(
      produce((state) => {
        state.PDFRenderState = type;
        if (type === 'preview') {
          state.isPreviewLoading = true;
          state.previewRenderState = true;
          state.previewFormIncomplete = formIncomplete;
          state.previewAnchorContainerId = resolvedStartContainerId;
        }
      }),
      false,
      'saveAndGeneratePdf'
    );
    let name;
    let res;
    if (currentDocument.type === 'template') {
      const templatesAndSections = templates.concat(sections);
      let index = templatesAndSections.findIndex(
        (obj) => obj.id === currentDocument.id
      );
      name = templatesAndSections[index].name;
      if (type !== 'preview') {
        await templatesApi.updateTemplate({
          id: currentDocument.id,
          name,
          boxes,
          masks,
          configuration,
          summaryConfiguration,
          gridConfiguration,
          templatable: currentTemplate.templatable,
          landscape: currentTemplate.landscape,
          pattern: currentTemplate.pattern,
          work: currentTemplate.work,
          containers,
          columns,
          rowContainers,
          document_type_ids: currentTemplate.document_type_ids,
          variables,
          variableGroups,
          variableOrderArray,
          triggers,
          // Sans eux, updateTemplate retombe sur ses défauts et l'export PDF
          // d'une section récursive la rendait ordinaire.
          isRecursive: currentTemplate?.isRecursive ?? false,
          recursiveModelName: currentTemplate?.recursiveModelName ?? null,
          recursiveAgreementType: currentTemplate?.recursiveAgreementType ?? null,
        });
      }
      res = await templatesApi.fetchPdf({
        currentDocument,
        boxes: boxesIncluded,
        pages,
        masks,
        configuration,
        summaryConfiguration,
        gridConfiguration,
        name,
        containers: containersIncluded,
        columns: columnsIncluded,
        // Non filtrées : ce sont les colonnes incluses qui déterminent quelles
        // lignes sont réellement rendues (via column.rowContainerIds).
        rowContainers,
        landscape,
        type,
        variables,
        variableGroups,
        variableOrderArray,
        triggers,
        currentTemplate,
        tmp,
      });
    } else {
      let index = proposals.findIndex((obj) => obj.id === currentProposal.id);
      name = proposals[index].name;
      if (type !== 'preview') {
        await proposalsApi.postProposal({
          currentDocument,
          currentProposal,
          boxes,
          masks,
          configuration,
          summaryConfiguration,
          gridConfiguration,
          containers,
          columns,
          rowContainers,
          variables,
          variableGroups,
          variableOrderArray,
          triggers,
        });
      }
      res = await proposalsApi.fetchPdf({
        currentDocument,
        currentProposal,
        boxes: boxesIncluded,
        pages,
        masks,
        configuration,
        summaryConfiguration,
        gridConfiguration,
        name,
        containers: containersIncluded,
        columns: columnsIncluded,
        // Non filtrées : ce sont les colonnes incluses qui déterminent quelles
        // lignes sont réellement rendues (via column.rowContainerIds).
        rowContainers,
        landscape,
        type,
        variables,
        variableGroups,
        variableOrderArray,
        triggers,
        formIncomplete,
      });
    }
    if (!res.ok) {
      message.error({
        content:
          type === 'preview'
            ? "Une erreur est survenue lors de la génération de l'aperçu"
            : 'Une erreur est survenue lors de la génération du PDF.',
        key: 'pdfKey',
      });
      set(
        produce((state) => {
          state.PDFRenderState = false;
          state.isPreviewLoading = false;
        }),
        false,
        'saveAndGeneratePdf'
      );
    }
    if (process.env.REACT_APP_LOCAL_PDF_URL) {
      set(
        produce((state) => {
          state.PDFRenderState = false;
          if (res.ok) {
            if (res.result?.tooLargeForResponse) {
              message.warning({
                content: `PDF généré (trop volumineux pour le retour local) : ${res.result.localPath}`,
                key: 'pdfKey',
              });
            } else {
              message.success({
                content:
                  type === 'preview'
                    ? "L'aperçu a bien été généré."
                    : 'Le PDF a bien été créé.',
                key: 'pdfKey',
              });
            }
            if (type === 'preview') {
              if (typeof pagesPreviewExcludedIdx === 'number') {
                state.pagesPreview = [
                  ...pagesPreviewExcluded,
                  ...formatPagesPreview({
                    pages: res.result.data,
                  }),
                ];
              } else {
                state.pagesPreview = formatPagesPreview({
                  pages: res.result.data,
                });
              }
              state.isPreviewLoading = false;
            } else {
              mutateClosePreview(state);
            }
          }
        }),
        false,
        'saveAndGeneratePdf'
      );
    }
    return res;
  },

  setStore: async (newStore) => {
    set(
      produce((state) => {
        Object.keys(newStore).forEach((k) => {
          state[k] = newStore[k];
        });
      }),
      false,
      'setStore'
    );
  },

  saveProposal: async () => {
    const {
      landscape,
      boxes,
      pages,
      masks,
      configuration,
      summaryConfiguration,
      gridConfiguration,
      variables,
      currentDocument,
      currentProposal,
      containers,
      columns,
      rowContainers,
      defaultVariablesApi,
      variableGroups,
      triggers,
      variableOrderArray,
    } = get();
    if (!currentDocument.id) return;

    const res = await proposalsApi.postProposal({
      currentDocument,
      currentProposal,
      landscape,
      boxes,
      pages,
      masks,
      configuration: (({ zoom, ...restConfiguration }) => restConfiguration)(
        configuration
      ),
      summaryConfiguration,
      gridConfiguration,
      containers,
      columns,
      rowContainers,
      variables,
      variableGroups,
      triggers,
      variableOrderArray,
    });
    res.content = formatResContent(
      res.content,
      defaultVariablesApi,
      false,
      i18n.t,
      landscape
    );
    const { id } = res;
    if (
      typeof res.hidden === 'boolean' &&
      get().currentProposal?.id === currentProposal.id
    ) {
      get().setDocumentHidden(res.hidden);
    }
    set(
      produce((state) => {
        state.currentProposal = { id };
        Object.keys(res.content).forEach((k) => {
          state[k] = res.content[k];
        });
      }),
      false,
      'saveProposal'
    );
    return res;
  },

  newProposal: async (document) => {
    // Vider les résultats de recherche lors de la création d'une nouvelle proposition
    get().clearSearchResults();
    get().clearTemplateCache();

    await get().fetchTables(document.documentType, document.id);
    set(
      produce((state) => {
        state.currentProposal = { id: null };
        state.currentDocument = document;
      }),
      false,
      'New Proposal (have to choose template)'
    );
  },

  changeCurrentTab: async (currentTab) => {
    set(
      produce((state) => {
        state.currentTab = currentTab;
      }),
      false,
      `Switched to tab ${currentTab}`
    );
  },

  setEventManagerEnabled: (value) => {
    if (value === get().eventManagerEnabled) return;
    set(
      produce((state) => {
        state.eventManagerEnabled = value;
      }),
      false,
      `Changed eventManagerEnabled to  ${value}`
    );
  },

  // `dedupeSectionStyles` : retire les doublons de styles de section avant
  // l'affichage puis enregistre le document d'un bloc (cf. applyTemplate).
  // Réservé aux copies fraîches (duplication), pas à une simple consultation.
  setProposal: async (id, { dedupeSectionStyles: dedupeStyles = false } = {}) => {
    const currentDocument = get().currentDocument;
    await get().fetchDefaultVariablesApi();
    let urlParams = {
      pid: id,
    };
    if (currentDocument.type === 'opportunity') {
      urlParams.oid = currentDocument.id;
    } else if (currentDocument.type === 'offer') {
      urlParams.ofid = currentDocument.id;
    } else {
      throw new Error(
        `Document type : ${currentDocument.type} is not handled by editor.`
      );
    }
    emptyUrl();
    updateUrl(urlParams);
    get().setProposalId(id);
    const fetchedProposal = await proposalsApi.fetchProposal(
      { id: id, type: currentDocument.type },
      get().defaultVariablesApi
    );
    const dedupedContent = dedupeStyles
      ? dedupeSectionStyles(fetchedProposal.content)
      : fetchedProposal.content;
    const sectionStylesDeduped = dedupedContent !== fetchedProposal.content;
    fetchedProposal.content = dedupedContent;
    await get().applyProposal(
      fetchedProposal,
      currentDocument.type,
      fetchedProposal.sections
    );
    if (sectionStylesDeduped) {
      try {
        await get().saveProposal();
      } catch (error) {
        console.error('Enregistrement du document nettoyé impossible', error);
      }
    }
  },

  // Restaure une version du content : le serveur écrit le content restauré,
  // sans garder l'état courant (revert pur, voulu côté web : seules les
  // sauvegardes auto le couvrent), puis on recharge via le loader
  // canonique. Celui-ci refait le transform raw→EditorState et met
  // isDocumentLoading → structureSync ne diffuse pas ce gros changement comme
  // une édition utilisateur (pas de diff massif vers les co-éditeurs).
  restoreContentVersion: async (versionId) => {
    const { currentDocument, currentProposal } = get();
    const isTemplate = currentDocument.type === 'template';
    const documentId = isTemplate ? currentDocument.id : currentProposal?.id;
    if (!documentId) return;

    // Restaurer remplace le content entier : refusé si un co-éditeur édite
    // quoi que ce soit (box, variable, configurateur, aperçu...). Retourne le
    // message de refus (même contrat que moveBoxesIndex).
    const remote = anyRemoteLockHolder(get());
    if (remote) {
      return `Restauration impossible : ${remote.name} est en train d'éditer le document`;
    }

    // Le flag pilote le claim du verrou partagé reserved:restore
    // (useEditorCollab) et l'overlay bloquant local (RestoreOverlay).
    get().setIsRestoringVersion(true);
    try {
      await versionsApi.restoreVersion({
        documentType: isTemplate ? 'template' : 'proposal',
        documentId,
        versionId,
      });

      if (isTemplate) {
        await get().applyTemplate(documentId);
      } else {
        await get().setProposal(currentProposal.id);
      }
      // La pile undo ne doit pas traverser un restore : un undo post-restore
      // appliquerait des inverses calculés sur l'état pré-restore.
      get().clearUndoHistory();
    } finally {
      get().setIsRestoringVersion(false);
    }
  },

  applyProposal: async (data, type, preloadedSections = null) => {
    // Vider les résultats de recherche lors du changement de proposition
    get().clearSearchResults();
    // Même raison que dans applyTemplate : le cache est fait pour durer un
    // document. Sans ce vidage, passer d'un document à l'autre (DocumentsPanel,
    // ProposalList) réinjectait des sections lues pour le précédent — périmées
    // si elles ont été modifiées entre-temps, ou déjà mutées/gelées par immer.
    get().clearTemplateCache();

    let {
      id,
      content,
      work,
      landscape,
      has_opportunity_option,
      has_opportunity_fa,
      has_company_work_fa,
      zone_names,
      document_name,
      opportunity_name,
      opportunity_chrono,
      offer_name,
      offer_chrono,
      hidden,
    } = data;
    let contentExists = true;
    if (!content) {
      content = defaultDocContent;
      contentExists = false;
    }

    // content.configuration.fontFamily =
    //   content.configuration?.fontFamily ?? defaultConfiguration.fontFamily;
    get().loadFont(content.configuration.fontFamily);
    // Needed before recursive hydration: child section validation depends on this map.
    await get().fetchRecursiveSectionRelationships();
    const sections = preloadedSections ?? (await getProposalSections(id));
    queryClient.setQueryData(
      ['Sections', { documentType: get().currentDocument.type, documentId: id }],
      sections
    );
    //Inject section if there are some in template
    let sectionBoxes;
    const recursiveSectionLinkInjections = [];
    if (content.boxes.some((box) => box.type === ItemTypes.SECTION_LINK)) {
      // Exclude baked SECTION_LINK boxes (created by mutateInjectSection, so they carry
      // fromBoxId). These belong to already-injected parent sections and are re-created
      // when their parent re-injects. Processing them here would inject their section a
      // second time on top of the existing baked content → duplication.
      //
      // Exception : une ancre bakée qui n'a AUCUN contenu déplié sous elle n'a été
      // injectée par personne — le test est celui qui sert déjà de garde
      // anti-doublon, donc le scénario ci-dessus (contenu présent) reste exclu.
      // C'est le cas d'une section RÉCURSIVE imbriquée : expandLinkedSectionsAtCreation
      // ne sait pas boucler sur les lignes du modèle et laisse l'ancre brute, à charge
      // du chargement de l'expanser. Ces ancres-là sont filtrées juste après (phase 1) :
      // seules les sections récursives sont réellement traitées.
      sectionBoxes = content.boxes.filter(
        (box) =>
          box.type === ItemTypes.SECTION_LINK &&
          (!box.fromBoxId ||
            !isRecursiveSectionExpandedAtAnchor(content.boxes, box.id, box.sectionId))
      );

      await Promise.all(
        sectionBoxes.map(async (box) => {
          if (!sections.some((section) => section.id === box.sectionId))
            return box;
          const section = await get().getTemplate(box.sectionId);
          if (!section) return box;
          const { isRecursive, recursiveModelName } =
            getSectionRecursiveMeta(section);
          if (
            isRecursive &&
            recursiveModelName &&
            get().currentDocument.type !== 'template'
          ) {
            if (
              !shouldSkipRecursiveReloadExpansion(content.boxes, {
                anchorBoxId: box.id,
                sectionId: box.sectionId,
                modelName: recursiveModelName,
              })
            ) {
              recursiveSectionLinkInjections.push({
                boxId: box.id,
                section: { ...section, isRecursive, recursiveModelName },
              });
            }
            return box;
          }
          // Ancre bakée non récursive : elle n'est ici que parce qu'elle n'a pas
          // de contenu déplié (cf. filtre ci-dessus). Son injection appartient à
          // sa section parente, on ne la traite pas — comportement d'avant
          // l'exception, préservé tel quel.
          if (box.fromBoxId) return box;
          const currentDocument = get().currentDocument;
          const sectionContent =
            currentDocument.type === 'template'
              ? section.content
              : {
                  ...section.content,
                  ...(await fillEmptyAdminTablesBoxes({
                    boxes: section.content.boxes,
                    containers: section.content.containers,
                    columns: section.content.columns,
                    proposable_id: currentDocument.id,
                    type: currentDocument.type,
                    landscape,
                    marginRight:
                      section.content.configuration.margins.right * gridUnit,
                    marginLeft:
                      section.content.configuration.margins.left * gridUnit,
                    header: section.content.masks.find(
                      ({ id }) => id === MASKS_TYPE.HEADER.id
                    ),
                    footer: section.content.masks.find(
                      ({ id }) => id === MASKS_TYPE.FOOTER.id
                    ),
                    gridConfiguration: section.content.gridConfiguration,
                    configuration: section.content.configuration,
                    triggers: section.content.triggers,
                    variableOrderArray: section.content.variableOrderArray,
                  })),
                };

          // Injection de la section dans le box
          box.sectionContent = sectionContent;
        })
      );
    }
    // Snapshot des collections telles qu'en DB (avant toute injection ET avant
    // évaluation des triggers ci-dessous), pour calculer le diff de ce que ce
    // load a changé (cf. plus bas, après hydrateRecursiveModels). Armé dès
    // qu'il existe un contenu réel : les ancres récursives (SECTION_VARIABLE
    // imbriquées, RECURSIVE_MODEL) ne passent pas forcément par un SECTION_LINK
    // de premier niveau, contrairement aux sections classiques — et les
    // triggers de section_variable (mutateSearchAndLaunchTrigger /
    // mutateExecuteParentTriggers, juste après) peuvent muter sectionId/answer/
    // activatedTriggerIds au même titre que l'expansion de SECTION_LINK, avec
    // le même risque de perte (mutation faite pendant isDocumentLoading, jamais
    // diffusée/persistée par structureSync — garde anti-data-loss).
    // Clonage superficiel obligatoire (par item, pas juste le tableau) : `content`
    // vient tout juste d'être assigné dans le draft immer via `state[k] = content[k]`
    // ci-dessous, or immer ne draft PAS les valeurs assignées en cours de recipe —
    // les mutations faites dessus par le code qui suit (find() + affectation directe
    // de champ) sont donc de VRAIES mutations en place sur ces mêmes objets, `content`
    // y compris (vérifié : sans ce clonage, `content.boxes[i].activatedTriggerIds` se
    // retrouve mutée après coup). Sans copie indépendante ici, rawStructure ne serait
    // qu'un alias corrompu par les mutations qu'il est censé permettre de détecter —
    // computeDiff (comparaison par référence) ne verrait jamais aucun changement.
    const rawStructure = contentExists
      ? {
          boxes: content.boxes.map((b) => ({ ...b })),
          containers: content.containers.map((c) => ({ ...c })),
          columns: content.columns.map((c) => ({ ...c })),
          masks: content.masks.map((m) => ({ ...m })),
          variableGroups: content.variableGroups.map((g) => ({ ...g })),
          variables: Object.fromEntries(
            Object.entries(content.variables).map(([k, v]) => [k, { ...v }])
          ),
          triggers: Object.fromEntries(
            Object.entries(content.triggers).map(([k, v]) => [k, [...v]])
          ),
          configuration: content.configuration,
          variableOrderArray: content.variableOrderArray,
        }
      : null;
    const validSectionIds = new Set(sections.map((section) => section.id));
    set(
      produce((state) => {
        state.currentProposal = state.currentDocument.id;
        state.landscape = landscape;
        state.hasOpportunityOption = !!has_opportunity_option;
        state.hasOpportunityFA = !!has_opportunity_fa;
        state.hasCompanyWorkFA = !!has_company_work_fa;
        state.zoneNames = zone_names ?? [];
        state.documentIdentity = {
          documentName: document_name ?? null,
          opportunityName: opportunity_name ?? null,
          opportunityChrono: opportunity_chrono ?? null,
          offerName: offer_name ?? null,
          offerChrono: offer_chrono ?? null,
          hidden: !!hidden,
        };
        Object.keys(content).forEach((k) => {
          state[k] = content[k];
        });
        if (contentExists) {
          state.configuration = withUniqueColors(
            syncStructure(content.configuration, defaultConfiguration)
          );
          state.summaryConfiguration = syncStructure(
            content.summaryConfiguration,
            defaultItemValues.summary.content.configuration
          );
          state.gridConfiguration = syncStructure(
            content.gridConfiguration,
            defaultItemValues
          );
          // state.pages = syncPageStructure(content.pages);
          if (state.hasOwnProperty('pages')) {
            delete state.pages;
          }
        }

        // Injection of linked sections in state
        if (sectionBoxes) {
          sectionBoxes.forEach((box) => {
            if (
              recursiveSectionLinkInjections.some(
                ({ boxId }) => boxId === box.id
              )
            ) {
              return;
            }
            // Ancre bakée conservée par l'exception du filtre mais non récursive :
            // rien à injecter, et surtout rien à supprimer — la branche de retrait
            // ci-dessous ne vise que les ancres de premier niveau dont la section
            // n'existe plus.
            if (box.fromBoxId) return;
            if (!sections.some((section) => section.id === box.sectionId)) {
              mutateRemmoveSectionContainers(state, box.id, {
                sectionId: box.sectionId,
                dateTimestamp: state.containers.find((container) =>
                  container.columnsIds.includes(box.columnId)
                ).dateTimestamp,
              });
            } else if (box.sectionContent) {
              // Le contenu n'est peuplé (phase 1) que si getTemplate a répondu.
              // Sans cette garde, une section listée mais dont le fetch a échoué
              // (course plus fréquente à 2+ éditeurs) injecterait un contenu
              // undefined → crash / box vide. On laisse alors le SECTION_LINK nu.
              mutateInjectSection({
                draft: state,
                sectionId: box.sectionId,
                boxId: box.id,
                sectionContent: box.sectionContent,
                linked_section: true,
                validSectionIds,
              });
              box.sectionContent = null;
            }
          });
        }

        state.currentProposal = { id, work };
        // mutateBoxesPageIndex(
        //   state.boxes,
        //   state.pages.map((p) => p.id)
        // );
        mutateBoxesOrderInfinite(state);
        mutateTitlesIndexes(state);
        mutateResizeSummary(state);
        Object.entries(state.variables).forEach(([key, value]) => {
          if (!value.notUsed) {
            mutateSearchAndLaunchTrigger({
              triggerId: key,
              draft: state,
            });
          }
        });
        state.boxes.forEach((b) => {
          if (isAVariableBox(b.type)) {
            mutateExecuteParentTriggers({
              draft: state,
              key: b.id,
            });
            mutateSearchAndLaunchTrigger({
              triggerId: b.id,
              draft: state,
            });
          }
        });
      }),
      false,
      'applyProposal'
    );
    set(
      produce((draft) => {
        mutateSetGridConfiguration(draft);
      }),
      false,
      'setGridConfiguration'
    );
    const expandedRecursiveAnchors = new Set();
    for (const { boxId, section } of recursiveSectionLinkInjections) {
      expandedRecursiveAnchors.add(boxId);
      await get().injectRecursiveSection({ section, boxId, split: false });
    }
    await get().hydrateEmbeddedRecursiveSections();
    await get().hydrateRecursiveModels();
    await get().expandRemainingRecursiveAnchors(expandedRecursiveAnchors);

    // Figer en DB ce que CE load a mutaté : sections liées classiques,
    // sections récursives, hydratation des sections imbriquées, modèles
    // récursifs offer, ET activation de section_variable par les triggers
    // évalués plus haut (mutateSearchAndLaunchTrigger / mutateExecuteParentTriggers).
    // Tout cela s'est fait pendant isDocumentLoading — donc n'a été ni diffusé
    // ni persisté par structureSync (garde anti-data-loss, cf. skipAndRebase/
    // isStableEditState). Sans ce rattrapage, le content DB garde les ancres
    // brutes et/ou sectionId/answer/activatedTriggerIds jamais écrits (alors
    // que le contenu de section injecté ensuite — hors isDocumentLoading — lui
    // est bien persisté normalement), et le proposal rejoue/perd ces mutations
    // à CHAQUE ouverture. On persiste ici, une seule fois, le diff complet de
    // ce que ce load a changé, via le même canal granulaire que l'autosave —
    // pas un blob. Calculé APRÈS toutes les injections ci-dessus (et non avant)
    // pour que ce diff capture aussi le contenu récursif.
    // Garde : on ne fige que si aucun co-éditeur n'est présent. Les containers/
    // colonnes injectés ont des uuid aléatoires ; deux clients qui figent en
    // concurrence produiraient deux upserts distincts → doublon d'expansion en
    // DB. Le créateur (seul au départ) fige ; les arrivants lisent l'expansion
    // déjà en DB. Si ce load n'a rien changé (cas normal d'une réouverture),
    // computeDiff renvoie null et rien n'est envoyé.
    if (rawStructure) {
      const s = get();
      const hasOtherEditor =
        s.collabConnected && s.presence.some((u) => u.id !== s.user?.id);
      if (!hasOtherEditor) {
        const diff = computeDiff(rawStructure, s);
        if (diff) {
          const structureDiff = diff.forward;
          setTimeout(() => persistStructureDiff(structureDiff), 0);
        }
      }
    }

    get().stopLoading();
    set(
      produce((state) => {
        state.isDocumentLoading = false;
      }),
      false,
      `isDocumentLoading false`
    );
    get().fetchRecursiveSectionRelationships();
    get().refreshIncompletePlanningScreenshots();
  },

  hydrateRecursiveModels: async () => {
    const { boxes, currentDocument } = get();
    if (currentDocument.type !== 'offer' || !currentDocument.id) return;

    const placeholders = boxes.filter((b) => b.type === ItemTypes.RECURSIVE_MODEL);
    if (placeholders.length === 0) return;

    const oppRows = await getRecursiveSectionData(currentDocument.id, 'opportunity', { documentType: 'offer' });
    if (!oppRows || oppRows.length === 0) return;

    const worksPresent = new Set(oppRows.map((r) => r.work).filter(Boolean));

    for (const placeholder of placeholders) {
      const forced = placeholder.content?.forcedTemplates ?? {};
      const missing = [...worksPresent].filter((w) => !forced[w]);

      if (missing.length === 0) {
        await get().injectRecursiveModel({ box: placeholder, templatesByWork: forced });
      } else {
        // Empiler, ne pas écraser : deux placeholders incomplets doivent chacun
        // obtenir leur modale (cf. pendingRecursiveModelChoices dans le store).
        set(
          produce((draft) => {
            draft.pendingRecursiveModelChoices.push({
              boxId: placeholder.id,
              missingWorks: missing,
              forcedTemplates: forced,
            });
          }),
          false,
          'enqueuePendingRecursiveModelChoice'
        );
      }
    }
  },

  /**
   * Déplie les ancres récursives apparues PENDANT le chargement.
   *
   * Le balayage d'applyProposal porte sur le content tel qu'il est en base. Or
   * chaque injection récursive bake à son tour les ancres de sa propre section
   * (les SECTION_LINK enfants), qui n'existaient donc pas au moment du balayage
   * — et mutateInjectSection ne les met pas en file, justement pour ne pas
   * réinjecter par-dessus le contenu déjà baké d'une section classique.
   *
   * Sans cette passe, ces ancres attendaient le chargement suivant : le
   * document gagnait un niveau d'imbrication à chaque ouverture (mesuré : 489
   * boxes à la création, 866 au 2e chargement, 1015 au 3e) avant de se
   * stabiliser une fois le dernier niveau atteint.
   */
  expandRemainingRecursiveAnchors: async (alreadyExpanded) => {
    if (get().currentDocument.type === 'template') return;

    // Une ancre n'est tentée qu'une fois par chargement : une section récursive
    // dont le modèle ne renvoie aucune ligne n'injecte rien et resterait donc
    // « non dépliée » à jamais. Marquer avant d'injecter borne la boucle : à
    // chaque passe il ne reste que les ancres nées de la passe précédente.
    for (let pass = 0; pass < RECURSIVE_ANCHOR_MAX_PASSES; pass += 1) {
      await waitForSectionsActivation(get);
      const boxes = get().boxes;
      const queued = new Set(get().sectionsToActivate.map(({ boxId }) => boxId));
      const pending = boxes.filter(
        (box) =>
          box.type === ItemTypes.SECTION_LINK &&
          box.sectionId &&
          !alreadyExpanded.has(box.id) &&
          !queued.has(box.id) &&
          !isRecursiveSectionExpandedAtAnchor(boxes, box.id, box.sectionId)
      );
      if (pending.length === 0) return;

      for (const box of pending) {
        alreadyExpanded.add(box.id);
        // L'ancre précédente a pu relancer la file : celle-ci a pu y être
        // dépliée (ou retirée) entre-temps.
        await waitForSectionsActivation(get);
        const currentBoxes = get().boxes;
        if (
          !currentBoxes.some((b) => b.id === box.id) ||
          get().sectionsToActivate.some(({ boxId }) => boxId === box.id) ||
          isRecursiveSectionExpandedAtAnchor(currentBoxes, box.id, box.sectionId)
        )
          continue;
        const section = await get().getTemplate(box.sectionId);
        if (!section || section.error) continue;
        const { isRecursive, recursiveModelName } = getSectionRecursiveMeta(section);
        // Les ancres non récursives sont l'affaire du drainage classique
        // (sectionsToActivate) : on ne fait que les écarter.
        if (!isRecursive || !recursiveModelName) continue;
        await get().injectRecursiveSection({
          section: { ...section, isRecursive, recursiveModelName },
          boxId: box.id,
          split: false,
        });
      }
    }
  },

  // Expanse les sections liées (SECTION_LINK) d'un content À LA CRÉATION du
  // proposal, dans un draft immer ISOLÉ (ne touche pas le store editor courant),
  // et renvoie le content expansé. But : le content stocké en DB ne contient
  // JAMAIS de SECTION_LINK brut → aucune ré-injection par client au load, donc
  // aucune divergence d'uuid entre co-éditeurs (plus de boxes vides ni de
  // suppression cassée). La création est mono-utilisateur → pas de course.
  // Réutilise la MÊME machinerie que le load (mutateInjectSection) et draine les
  // sections imbriquées comme le fait le middleware (store.js) + handleSectionAction.
  // Le content attendu est au format editorState (sortie de getTemplate /
  // formatResContent), comme applyProposal ; la sérialisation raw reste au POST.
  // `sections` : catalogue déjà chargé (rechargement des sections d'un
  // document), sinon celui du modèle. `onUnreadableSection(ancreId)` : une
  // section du catalogue n'a pas pu être lue (elle est sautée, comme avant).
  expandLinkedSectionsAtCreation: async ({
    content,
    templateId,
    sections: knownSections,
    onUnreadableSection,
    documentType,
    documentId,
    landscape,
  }) => {
    if (!content.boxes?.some((b) => b.type === ItemTypes.SECTION_LINK))
      return content;

    const getTemplate = get().getTemplate;
    const currentDocument = { id: documentId, type: documentType };
    const sections = knownSections ?? (await getTemplateSections(templateId));
    // Ancres copiées puis marquées avant injection : le contenu injecté hérite
    // du marqueur et le document pourra recharger ses sections liées (cf.
    // utils/linkedSectionZones).
    const anchors = snapshotLinkedSectionAnchors(content);

    // Draft isolé : uniquement le slice lu/écrit par mutateInjectSection.
    let draft = {
      boxes: content.boxes,
      containers:
        documentType === 'template'
          ? content.containers
          : markLinkedSectionAnchors(content.containers, anchors),
      columns: content.columns,
      rowContainers: content.rowContainers ?? [],
      masks: content.masks,
      variables: content.variables,
      variableGroups: content.variableGroups,
      variableOrderArray: content.variableOrderArray ?? [],
      triggers: content.triggers,
      configuration: content.configuration,
      gridConfiguration: content.gridConfiguration,
      summaryConfiguration: content.summaryConfiguration,
      landscape,
      sections,
      currentDocument,
      isDocumentLoading: false,
      fromPdf: false,
      itemsChangedByTriggers: [],
      sectionsToActivate: [],
    };

    // Une section RÉCURSIVE ne s'injecte pas telle quelle : son contenu est un
    // gabarit à répéter pour chaque ligne du modèle (un poste de travail, une
    // tenue…), avec substitution des variables `modele.champ`. Cette mécanique
    // vit dans injectRecursiveSection (store/sections.js), qui a besoin du store
    // (fetch des lignes, variables indexées) alors qu'on travaille ici sur un
    // draft isolé, hors store, avant même que le document existe.
    //
    // On laisse donc l'ancre SECTION_LINK brute : applyProposal la déplie au
    // premier chargement, puis fige l'expansion en base (cf. le gel du diff, plus
    // bas dans applyProposal). C'est le comportement d'avant l'expansion à la
    // création — sans ce report, la section était bakée en un seul exemplaire,
    // variables non substituées, et la garde anti-réexpansion du chargement
    // (isRecursiveSectionExpandedAtAnchor) verrouillait définitivement cet état.
    const isDeferredRecursive = async (sectionId) => {
      if (documentType === 'template') return false;
      const section = await getTemplate(sectionId); // mémoïsé (_templateCache)
      if (!section || section.error) return false;
      const { isRecursive, recursiveModelName } = getSectionRecursiveMeta(section);
      return !!(isRecursive && recursiveModelName);
    };

    // Phase 1 : fetch du contenu de chaque section liée de 1er niveau.
    const topLinks = draft.boxes.filter(
      (b) => b.type === ItemTypes.SECTION_LINK
    );
    const topContents = await Promise.all(
      topLinks.map(async (b) => {
        if (!sections.some((s) => s.id === b.sectionId)) return null;
        if (await isDeferredRecursive(b.sectionId)) return null;
        const sectionContent = await buildSectionContentForInjection({
          getTemplate,
          sectionId: b.sectionId,
          currentDocument,
          landscape,
        });
        if (!sectionContent) onUnreadableSection?.(b.id);
        return sectionContent;
      })
    );

    // Phase 2 : injection des sections de 1er niveau (immer).
    const validSectionIds = new Set(sections.map((s) => s.id));
    draft = produce(draft, (d) => {
      topLinks.forEach((b, i) => {
        if (!sections.some((s) => s.id === b.sectionId)) {
          mutateRemmoveSectionContainers(d, b.id, {
            sectionId: b.sectionId,
            dateTimestamp: d.containers.find((c) =>
              c.columnsIds.includes(b.columnId)
            )?.dateTimestamp,
          });
        } else if (topContents[i]) {
          mutateInjectSection({
            draft: d,
            sectionId: b.sectionId,
            boxId: b.id,
            sectionContent: topContents[i],
            linked_section: true,
            validSectionIds,
          });
        }
      });
    });

    // Drainage des sections imbriquées : mêmes règles que le middleware
    // (store.js) + handleSectionAction (store/sections.js). La garde
    // mutateHandleLinkedSectionRecursive empêche les boucles infinies.
    // Gère les 3 actions comme handleSectionAction (pas seulement 'inject') :
    // l'expansion d'une section liée peut, via une variable partagée avec une
    // AUTRE section_variable du document (mutateInjectSection réévalue les
    // triggers de chaque variable injectée sur tout le document, cf.
    // sections.js), faire pousser une tâche 'reinitialize'/'remove' dans
    // sectionsToActivate — un trigger de section_variable totalement sans
    // rapport avec la section liée en cours d'expansion. Avant ce fix, ces
    // tâches étaient silencieusement ignorées (`continue`), laissant la
    // section_variable activée en métadonnées (sectionId/answer/
    // activatedTriggerIds déjà posés par le trigger) mais sans jamais recevoir
    // son contenu injecté.
    while (draft.sectionsToActivate.length > 0) {
      const task = draft.sectionsToActivate[0];
      draft = produce(draft, (d) => {
        d.sectionsToActivate.shift();
      });
      if (task.action === 'remove') {
        draft = produce(draft, (d) => {
          const box = d.boxes.find((b) => b.id === task.boxId);
          if (box?.type === ItemTypes.SECTION_VARIABLE && task.optionId) {
            box.answer = task.optionId;
          }
          mutateDeleteSection({ draft: d, boxId: task.boxId, optionId: task.optionId });
        });
        continue;
      }
      // Même report que pour les sections de 1er niveau : l'ancre bakée reste
      // brute et applyProposal l'expanse au chargement (son filtre d'ancres
      // accepte les ancres bakées sans contenu déplié, précisément pour ça).
      if (await isDeferredRecursive(task.sectionId)) continue;
      const sc = await buildSectionContentForInjection({
        getTemplate,
        sectionId: task.sectionId,
        currentDocument,
        landscape,
      });
      if (!sc) {
        onUnreadableSection?.(task.fromBoxId);
        continue;
      }
      draft = produce(draft, (d) => {
        const box = d.boxes.find((b) => b.id === task.boxId);
        if (!box) return;
        if (task.action === 'reinitialize') {
          mutateDeleteSection({ draft: d, boxId: task.boxId, optionId: task.optionId });
        }
        if (task.linked_section) {
          const isLoop = mutateHandleLinkedSectionRecursive({
            sectionsIds: task.sectionsIds,
            box,
            draft: d,
            fromBoxId: task.fromBoxId,
            boxId: task.boxId,
            sectionContent: sc,
            isSecondInjection: task.isSecondInjection,
          });
          if (isLoop) return;
        }
        mutateInjectSection({
          draft: d,
          sectionContent: sc,
          boxId: task.boxId,
          sectionId: task.sectionId,
          areItemsChangedByTriggers: task.areItemsChangedByTriggers,
          linked_section: task.linked_section,
          fromSectionId: task.fromSectionId,
          dateTimestamp: task.dateTimestamp,
          fromBoxId: task.fromBoxId,
          from_linked_section: task.from_linked_section,
          sectionsIds: task.sectionsIds,
          wasDocumentLoading: task.wasDocumentLoading,
          validSectionIds: task.validSectionIds,
        });
      });
    }

    // On ne remplace que les collections touchées par l'injection.
    // configuration incluse : mutateMergeStyles y merge les styles suffixés
    // (_section_{id}_time_{ts}) référencés par les boxes injectées — sans elle,
    // le content persisté référence des styles inexistants (checkbox invisibles).
    return {
      ...content,
      boxes: draft.boxes,
      containers: draft.containers,
      columns: draft.columns,
      rowContainers: draft.rowContainers,
      variables: draft.variables,
      variableGroups: draft.variableGroups,
      variableOrderArray: draft.variableOrderArray,
      triggers: draft.triggers,
      masks: draft.masks,
      configuration: draft.configuration,
    };
  },

  setProposalId: (id) => {
    set(
      produce((state) => {
        state.currentProposal = { id: id };
        state.isDocumentLoading = true;
      }),
      false,
      `Set proposal ID to ${id} to match PID`
    );
  },

  clearTemplateCache: () => _templateCache.clear(),

  getTemplate: async (id) => {
    if (_templateCache.has(id)) return _templateCache.get(id);
    if (!get().defaultVariablesApi) {
      await get().fetchDefaultVariablesApi();
    }
    const promise = templatesApi
      .getTemplate(
        id,
        get().defaultVariablesApi,
        get().currentDocument.type === 'template'
      )
      .then(withSyncedConfiguration);
    _templateCache.set(id, promise);
    // Seul point de passage commun à toutes les injections : on y retient le nom
    // pour que mutateInjectSection puisse nommer les styles importés même quand
    // la liste des sections n'est pas encore chargée.
    promise.then((t) => rememberSectionName(id, t?.name)).catch(() => {});
    return promise;
  },
  getTemplates: async () => {
    const { currentDocument } = get();
    let templatesRequest;
    if (currentDocument.type === 'template') {
      templatesRequest = templatesApi.getTemplates();
    } else if (currentDocument.type === 'opportunity') {
      templatesRequest = templatesApi.getOpportunityTemplates(currentDocument.id);
    } else {
      templatesRequest = templatesApi.getOfferTemplates(currentDocument.id);
    }
    // Dossiers de rangement (arbre partagé company) demandés en même temps et
    // posés dans le même set : chargés après coup, l'arbre s'affichait d'abord
    // à plat (tout à la racine) puis se réorganisait sous les yeux. Leur
    // échec ne bloque pas l'affichage des modèles/sections.
    const [res, folders] = await Promise.all([
      templatesRequest,
      foldersApi.getFolders(),
    ]);

    set(
      produce((state) => {
        // Posé même en échec : la liste quitte son spinner pour son état vide
        // au lieu de tourner indéfiniment.
        state.templatesLoaded = true;
        if (folders) state.folders = folders;
        if (!res) return;
        //séparation des Sections et des Templates pour simplifier l'affichage
        state.sections = [];
        state.templates = [];
        res.forEach((element) => {
          if (!element.work) element.work = '';
          if (element.pattern === 'section') {
            state.sections.push(element);
          } else if (element.pattern === 'template') {
            state.templates.push(element);
          }
        });
      }),
      false,
      `Set sections and templates`
    );
  },
  getFolders: async () => {
    const res = await foldersApi.getFolders();
    if (!res) return;
    set(
      produce((state) => {
        state.folders = res;
      }),
      false,
      `Set folders`
    );
  },
  getHiddenTemplates: async () => {
    const res = await templatesApi.getHiddenTemplates();
    if (!res) return;
    set(
      produce((state) => {
        state.hiddenTemplates = [];
        state.hiddenSections = [];
        res.forEach((element) => {
          if (!element.work) element.work = '';
          if (element.pattern === 'section') {
            state.hiddenSections.push(element);
          } else if (element.pattern === 'template') {
            state.hiddenTemplates.push(element);
          }
        });
      }),
      false,
      `Set hidden templates and sections`
    );
  },
  restoreTemplate: async (id) => {
    const success = await templatesApi.restoreTemplate(id);
    if (success) {
      // Recharger les templates et sections normaux
      await get().getTemplates();
      // Recharger les templates cachés
      await get().getHiddenTemplates();
    }
    return success;
  },
  deleteTemplate: async (id) => {
    await templatesApi.deleteTemplate(id);
    await get().getTemplates();
  },
  updateTemplate: async () => {
    const {
      landscape,
      currentTemplate,
      boxes,
      pages,
      masks,
      configuration,
      summaryConfiguration,
      gridConfiguration,
      containers,
      columns,
      rowContainers,
      variables,
      variableGroups,
      variableOrderArray,
      triggers,
    } = get();
    await templatesApi.updateTemplate({
      ...currentTemplate,
      landscape,
      boxes,
      pages,
      masks,
      configuration,
      summaryConfiguration,
      gridConfiguration,
      containers,
      columns,
      rowContainers,
      variables,
      variableGroups,
      variableOrderArray,
      triggers,
      isRecursive: currentTemplate?.isRecursive ?? false,
      recursiveModelName: currentTemplate?.recursiveModelName ?? null,
      recursiveAgreementType: currentTemplate?.recursiveAgreementType ?? null,
    });
  },
  updateTemplateRecursiveSettings: ({ isRecursive, recursiveModelName, recursiveAgreementType }) => {
    set(
      produce((draft) => {
        if (!draft.currentTemplate) return;
        draft.currentTemplate.isRecursive = isRecursive;
        draft.currentTemplate.recursiveModelName = recursiveModelName ?? null;
        draft.currentTemplate.recursiveAgreementType = recursiveAgreementType ?? null;
      }),
      false,
      'updateTemplateRecursiveSettings'
    );
  },
  setRecursiveSectionChild: (boxId, section) => {
    set(
      produce((draft) => {
        const box = draft.boxes.find((b) => b.id === boxId);
        if (!box) return;
        box.content = {
          ...box.content,
          childTemplateId: section.id,
          childTemplateName: section.name,
          childModelName: section.recursiveModelName ?? null,
        };
      }),
      false,
      `setRecursiveSectionChild ${boxId}`
    );
  },
  fetchRecursiveSectionFields: async (modelName, category = null) => {
    if (!modelName) return;
    const cacheKey = effectiveRecursiveModelKey(modelName, category);
    if (get().recursiveModelFields[cacheKey]) return;
    const fields = await getRecursiveSectionFields(modelName, category);
    set(
      produce((draft) => {
        draft.recursiveModelFields[cacheKey] = fields;
      }),
      false,
      `fetchRecursiveSectionFields ${cacheKey}`
    );
  },
  syncRecursiveVariablesToStore: (modelName, usedFieldKeys = null, category = null) => {
    const fields = get().recursiveModelFields[effectiveRecursiveModelKey(modelName, category)];
    if (!fields) return;
    const isTemplate = get().currentDocument?.type === 'template';
    const modelLabel = get().recursiveModelLabels[modelName] ?? modelName;
    const groupId = `recursive-group-${modelName}`;
    set(
      produce((draft) => {
        const variableKeys = [];
        fields.forEach((field) => {
          if (field.type === 'image') return;
          const key = `${modelName}.${field.key}`;
          variableKeys.push(key);
          // Non-indexed placeholders (value {{model.field}}) are palette helpers for template
          // authoring only. In a proposal the real data lives in indexed keys (model.N.field),
          // so the placeholder must stay hidden from the form whatever was persisted.
          const isUsed = isTemplate && usedFieldKeys ? usedFieldKeys.has(field.key) : false;
          if (!draft.variables[key]) {
            draft.variables[key] = {
              name: field.label,
              value: `{{${key}}}`,
              isRecursive: true,
              // Fields referenced in the section content must be visible in the form (template only).
              notUsed: isTemplate ? (usedFieldKeys ? !isUsed : true) : true,
            };
          } else {
            draft.variables[key].name = field.label;
            draft.variables[key].isRecursive = true;
            if (!isTemplate) {
              draft.variables[key].notUsed = true;
            } else if (isUsed) {
              delete draft.variables[key].notUsed;
            }
            // notUsed otherwise preserved (template): cleared when user inserts via @
          }
        });
        const groupExists = draft.variableGroups.some((g) => g.id === groupId);
        if (!groupExists) {
          mutateAddVariableGroup({ draft, id: groupId, name: modelLabel });
          const group = draft.variableGroups.find((g) => g.id === groupId);
          if (group) group.variableIds = variableKeys;
        }
      }),
      false,
      `syncRecursiveVariablesToStore ${modelName}`
    );
  },
  // usedFieldKeys must be derived from template content (section.content.boxes), never from
  // canvas boxes — substitution removes VARIABLE entities from injected boxes so a canvas scan
  // always returns an empty set. Pass it on first inject; subsequent calls fall back to the
  // value persisted in recursiveUsedFieldKeys[modelName].
  syncIndexedRecursiveVarsToStore: async (modelName, rows, usedFieldKeys, append = false, category = null) => {
    const storedKeys = get().recursiveUsedFieldKeys[modelName];
    const resolvedFieldKeys = usedFieldKeys ?? (storedKeys ? new Set(storedKeys) : null);
    const cacheKey = effectiveRecursiveModelKey(modelName, category);
    let fields = get().recursiveModelFields[cacheKey];
    if (!fields) {
      await get().fetchRecursiveSectionFields(modelName, category);
      fields = get().recursiveModelFields[cacheKey];
    }
    if (!fields || !rows?.length) return;
    const modelLabel = get().recursiveModelLabels[modelName] ?? modelName;

    set(
      produce((draft) => {
        if (!append) {
          const prefix = `${modelName}.`;
          Object.keys(draft.variables).forEach((key) => {
            if (isIndexedRecursiveKey(key, modelName)) {
              delete draft.variables[key];
              return;
            }
            // Non-indexed placeholder ({{model.field}}) is a template-authoring helper.
            // It must not survive in a proposal next to the indexed instances (model.N.field),
            // even if it was created mid-session (re-inject without a full reload).
            const v = draft.variables[key];
            if (
              key.startsWith(prefix) &&
              v?.isRecursive &&
              typeof v.value === 'string' &&
              /^\{\{.*\}\}$/.test(v.value.trim())
            ) {
              delete draft.variables[key];
            }
          });
          draft.variableGroups = draft.variableGroups.filter(
            (g) =>
              !g.id.startsWith(`recursive-group-${modelName}-`) &&
              g.id !== `recursive-group-${modelName}`
          );
        }
        const offset = append
          ? draft.variableGroups.filter((g) => g.id.startsWith(`recursive-group-${modelName}-`)).length
          : 0;
        rows.forEach((row, rowIndex) => {
          const absoluteIndex = rowIndex + offset;
          const groupId = `recursive-group-${modelName}-${absoluteIndex}`;
          const groupName =
            row.name ?? row.title ?? row.label ?? `${modelLabel} ${absoluteIndex + 1}`;
          const variableIds = [];

          fields.forEach((field) => {
            if (field.type === 'image') return;
            const key = `${modelName}.${absoluteIndex}.${field.key}`;
            variableIds.push(key);
            // Bind strictly by field key. The field set is fetched with the same category as
            // the rows (effectiveRecursiveModelKey), so keys align. No positional fallback:
            // a missing key renders empty rather than borrowing an unrelated column's value,
            // which silently produced wrong data under the wrong label.
            const value = row[field.key];
            const strValue = value != null ? String(value) : '';
            draft.variables[key] = {
              name: field.label,
              value: strValue,
              apiValue: strValue,
              isRecursive: true,
              notUsed: resolvedFieldKeys ? !resolvedFieldKeys.has(field.key) : false,
            };
          });
          mutateAddVariableGroup({ draft, id: groupId, name: groupName });
          const group = draft.variableGroups.find((g) => g.id === groupId);
          if (group) group.variableIds = variableIds;
        });
      }),
      false,
      `syncIndexedRecursiveVarsToStore ${modelName}`
    );
  },
  fetchRecursiveSectionRelationships: async () => {
    if (Object.keys(get().recursiveSectionRelationships).length > 0) return;
    const { relationships, modelLabels } = await getRecursiveSectionRelationships();
    set(
      produce((draft) => {
        draft.recursiveSectionRelationships = relationships;
        draft.recursiveModelLabels = modelLabels;
      }),
      false,
      'fetchRecursiveSectionRelationships'
    );
  },
  fetchAvailableRootModels: async (work, templatable) => {
    return getAvailableRootModels(work, templatable);
  },
  postTemplate: async ({
    name,
    templatable,
    templatePattern,
    landscape = false,
    work,
    documentTypeIds,
    isRecursive = false,
    recursiveModelName = null,
    recursiveAgreementType = null,
    folderId = null,
    scratch = undefined,
    adminOnly,
  }) => {
    const {
      boxes,
      pages,
      masks,
      configuration,
      summaryConfiguration,
      gridConfiguration,
      containers,
      columns,
      variables,
      variableGroups,
      variableOrderArray,
      triggers,
    } = get();
    const res = await templatesApi.postTemplate({
      name,
      templatable,
      work,
      pattern: formatTemplatePattern(templatePattern),
      landscape,
      boxes,
      pages,
      masks,
      configuration,
      summaryConfiguration,
      gridConfiguration,
      containers,
      columns,
      documentTypeIds,
      variableGroups,
      variables,
      variableOrderArray,
      triggers,
      isRecursive,
      recursiveModelName,
      recursiveAgreementType,
      folderId,
      scratch,
      adminOnly,
    });
    if (!res) return;
    updateUrl({ tid: res.id });
    await get().fetchDefaultVariablesApi();
    set(
      produce((state) => {
        state.currentTemplate = {
          name: res.name,
          id: res.id,
          templatable: res.templatable,
          pattern: res.pattern,
          landscape: res.landscape,
          work: work,
          preview: res.preview,
          document_type_ids: res.document_type_ids,
          isRecursive,
          recursiveModelName,
          recursiveAgreementType,
        };
        state.work = res.work;
        state.landscape = res.landscape;
        state.currentProposal = { id: null };
        const { variables, variableGroups } = formatVariables({
          content: res.content,
          defaultVariablesApi: state.defaultVariablesApi,
          currentDocIsTemplate: true,
        });
        state.variables = variables;
        state.variableGroups = variableGroups;
      }),
      false,
      'postTemplate'
    );
    if (recursiveModelName) {
      await get().fetchRecursiveSectionFields(recursiveModelName, recursiveAgreementType);
      get().syncRecursiveVariablesToStore(recursiveModelName, null, recursiveAgreementType);
    }
    await get().getTemplates();
    await get().fetchTables('template', res.id);
  },
  // patchTemplate: async (id, value) => {
  //   const res = await templatesApi.patchTemplate(id, value);
  //   if (!res) return;
  //   await get().getTemplates();
  // },
  fetchTables: async () => {
    const { id, type } = get().currentDocument;
    if (!id) return;
    const res = await documentDatasApi.getTables(type, id);
    set(
      produce((state) => {
        state.tables = res.tables;
        state.splittableTables = res.splittable_tables;
        state.zoneSplitTables = res.zone_split_tables || [];
        state.bpuCapabilities = res.bpu_capabilities || null;
      }),
      false,
      'fetchTables'
    );
  },
  /**
   * Recharger toutes les variables provenant de l'API (mode proposal uniquement)
   * Logique alignée avec ItemRefresher pour le calcul des valeurs
   */
  // Recharge les sections liées du document avec leur version actuelle : chaque
  // zone (cf. utils/linkedSectionZones) est remplacée, blocs ajoutés à la main
  // compris. Comme une restauration de version, le nouveau contenu est envoyé
  // en entier sans passer par le store, puis le document est rouvert : rien
  // n'est diffusé aux co-éditeurs comme une édition, et un échec laisse le
  // document ouvert intact. La réouverture déplie aussi les sections
  // récursives, comme à la création. À appeler après flushPendingSaves.
  // Retourne { error } ; { unreadable: n } quand des sections n'ont pas pu être
  // lues (rien n'est modifié : à l'utilisateur de choisir, puis rappel avec
  // skipUnreadable pour recharger les autres) ; {} sinon.
  // keepAnswers : les questions des zones rechargées reprennent leur réponse
  // (cf. restoreLinkedSectionAnswers), sinon elles reviennent comme dans la
  // bibliothèque.
  reloadLinkedSections: async ({ skipUnreadable = false, keepAnswers = false } = {}) => {
    const { currentDocument, currentProposal } = get();
    const proposalId = currentProposal?.id;
    if (!proposalId || currentDocument.type === 'template') return {};
    // Document en cours d'ouverture (le store peut encore porter le contenu du
    // précédent) ou section de question en cours d'injection.
    const busy = (s) =>
      s.isDocumentLoading ||
      s.sectionsToActivate?.length > 0 ||
      s.currentProposal?.id !== proposalId;
    // Le contenu entier est remplacé : seul sur le document (même garde que le
    // gel de l'expansion au chargement, cf. applyProposal).
    const hasOtherEditor = (s) =>
      s.collabConnected && s.presence.some((u) => u.id !== s.user?.id);
    const BUSY = 'Le document est en cours de mise à jour : réessayez dans un instant.';
    const SHARED = "Rechargement impossible : d'autres personnes ont ce document ouvert.";
    if (busy(get())) return { error: BUSY };
    if (hasOtherEditor(get())) return { error: SHARED };
    if (!groupLinkedSectionZones(get().containers).length) return {};

    get().setLoaderPdfToImage(true);
    let saved = false;
    try {
      // Une section retirée du catalogue serait supprimée par l'injection
      // (lien mort) : sa zone reste telle quelle.
      const sections = await getProposalSections(proposalId);
      const available = new Set(sections.map(({ id }) => id));
      // Version actuelle de la bibliothèque, pas celle mise en cache à
      // l'ouverture.
      get().clearTemplateCache();

      // Zones lues après les appels, sur l'état qui sera remplacé.
      const state = get();
      if (busy(state)) return { error: BUSY };
      if (hasOtherEditor(state)) return { error: SHARED };
      const zones = groupLinkedSectionZones(state.containers);
      const readyZones = zones.filter(({ anchors }) =>
        anchors.every(({ sectionId }) => available.has(sectionId))
      );
      if (!readyZones.length) {
        return { error: "Aucune section liée de ce document n'est encore disponible." };
      }
      const snapshot = {
        boxes: state.boxes,
        containers: state.containers,
        columns: state.columns,
        rowContainers: state.rowContainers,
        masks: state.masks,
        variables: state.variables,
        variableGroups: state.variableGroups,
        variableOrderArray: state.variableOrderArray,
        triggers: state.triggers,
        configuration: state.configuration,
        gridConfiguration: state.gridConfiguration,
        summaryConfiguration: state.summaryConfiguration,
      };
      // Préparé hors du store : une section illisible, sous-sections
      // comprises, est repérée avant la moindre modification.
      const prepare = async (groups) => {
        const unreadable = new Set();
        const expanded = await get().expandLinkedSectionsAtCreation({
          content: restoreLinkedSectionAnchors(snapshot, groups, currentDocument),
          sections,
          onUnreadableSection: (anchorId) => unreadable.add(anchorId),
          documentType: currentDocument.type,
          documentId: currentDocument.id,
          landscape: state.landscape,
        });
        const hit = groups.filter(({ anchors }) =>
          anchors.some(({ anchorId }) => unreadable.has(anchorId))
        );
        return { expanded, hit, unreadable: unreadable.size };
      };
      let groups = readyZones;
      let prepared = await prepare(groups);
      if (prepared.unreadable) {
        if (!skipUnreadable) return { unreadable: prepared.hit.length || prepared.unreadable };
        groups = groups.filter((group) => !prepared.hit.includes(group));
        if (!groups.length) {
          return { error: "Aucune section liée de ce document n'a pu être lue : rien n'a été modifié." };
        }
        prepared = await prepare(groups);
        if (prepared.unreadable) {
          return { error: "Une section n'a pas pu être lue dans la bibliothèque : rien n'a été modifié." };
        }
      }

      // Dernier contrôle avant d'écraser : rien n'a bougé pendant la préparation.
      const now = get();
      if (busy(now) || now.containers !== state.containers) {
        return { error: 'Le document a changé pendant la préparation : réessayez.' };
      }
      if (hasOtherEditor(now)) return { error: SHARED };

      const expanded = dedupeSectionStyles(prepared.expanded);
      await proposalsApi.postProposal({
        currentDocument,
        currentProposal,
        ...expanded,
        gridConfiguration: expanded.gridConfiguration,
        configuration: (({ zoom, ...rest }) => rest)(expanded.configuration),
      });
      saved = true;
      await get().setProposal(proposalId);
      // Relevées sur l'état remplacé, reposées sur le document rouvert.
      const unrestored = keepAnswers
        ? await get().restoreLinkedSectionAnswers(
            captureLinkedSectionAnswers(state.boxes, zonesAnchorIds(groups)),
            zonesAnchorIds(groups)
          )
        : 0;
      // Annuler ramènerait l'ancien contenu des sections à côté du nouveau.
      get().clearUndoHistory();
      if (unrestored)
        message.warning(
          `${unrestored} réponse(s) n'ont pas pu être reprises : la question ou l'option n'existe plus.`
        );
      const skipped = zones.length - groups.length;
      if (skipped)
        message.warning(
          `${skipped} section(s) liée(s) n'ont pas été rechargées : elles restent telles quelles.`
        );
      else message.success('Les sections liées ont été rechargées.');
      return {};
    } catch (error) {
      console.error(error);
      return {
        error: saved
          ? "Les sections liées ont été rechargées, mais le document n'a pas pu être rouvert : rechargez la page."
          : "Les sections liées n'ont pas pu être rechargées : rien n'a été modifié.",
      };
    } finally {
      get().setLoaderPdfToImage(false);
    }
  },

  // Repose les réponses relevées avant un rechargement des sections liées, par
  // le même chemin qu'un clic (updateBox / handleSectionAction) : les triggers
  // injectent les mêmes sections, et l'autosave les enregistre. Par passes :
  // la question d'une section ajoutée par une réponse n'existe qu'une fois
  // cette réponse reposée et la section injectée. Chaque réponse n'est reposée
  // qu'une fois. Retourne le nombre de réponses non reprises (question ou
  // option disparue de la bibliothèque).
  restoreLinkedSectionAnswers: async (captured, anchorIds) => {
    let pending = captured;
    let unrestored = 0;
    for (let pass = 0; pass < ANSWER_RESTORE_MAX_PASSES && pending.length; pass += 1) {
      await waitForSectionsActivation(get);
      const questions = indexLinkedSectionQuestions(get().boxes, anchorIds);
      const found = pending.filter(({ key }) => questions.has(key));
      if (!found.length) break;
      pending = pending.filter((entry) => !found.includes(entry));
      const updates = [];
      const sectionAnswers = [];
      for (const entry of found) {
        const box = questions.get(entry.key);
        const plan = planAnswerRestore(entry, box);
        if (plan.status === 'missing') unrestored += 1;
        if (plan.status === 'select') updates.push({ id: box.id, fn: selectAnswer(plan.ids) });
        if (plan.status === 'answer') sectionAnswers.push({ box, plan });
      }
      if (updates.length) get().batchUpdateBox(updates);
      for (const { box, plan } of sectionAnswers) {
        await waitForSectionsActivation(get);
        await get().handleSectionAction({
          optionId: plan.optionId,
          boxId: box.id,
          sectionId: box.sectionId,
          action: plan.label === 'Oui' ? 'inject' : 'remove',
        });
      }
    }
    await waitForSectionsActivation(get);
    return unrestored + pending.length;
  },

  reloadAllApiVariables: async () => {
    const { currentDocument } = get();

    // Fonctionnalité disponible uniquement en mode proposal
    if (!currentDocument.id || currentDocument.type === 'template') return;

    try {
      // Bloquer l'application avec le loader global
      get().setLoaderPdfToImage(true);

      // Rafraîchir les données API de base (même point d'entrée que ItemRefresher)
      await get().fetchDefaultVariablesApi();
      await get().fetchTables();

      // 1) Appliquer la logique d'ItemRefresher à toutes les variables issues de l'API
      set(
        produce((draft) => {
          const { defaultVariablesApi } = draft;
          if (!defaultVariablesApi) return;

          const variableIds = Object.keys(draft.variables || {});

          variableIds.forEach((variableId) => {
            const variable = draft.variables[variableId];
            if (!variable) return;

            const keys = variableId.split('.');
            // ItemRefresher ne traite que les clés à 2 ou 3 segments
            if (keys.length !== 2 && keys.length !== 3) return;

            const categoryKey = keys.length === 3 ? keys[1] : keys[0];
            const informationKey = keys.length === 3 ? keys[2] : keys[1];

            // Exclure toutes les variables customs du traitement global
            if (categoryKey === 'custom') {
              return;
            }

            let newValue;

            // Cas opportunité : CHRONO.categorie.information
            if (
              keys.length === 3 &&
              Array.isArray(defaultVariablesApi.opportunities_variables)
            ) {
              const indexKey =
                defaultVariablesApi.opportunities_variables.findIndex((opp) => {
                  const oppKey = Object.keys(opp || {})[0];
                  const opportunity = opp?.[oppKey]?.opportunity;
                  return opportunity && opportunity.CHRONO === keys[0];
                });

              if (indexKey !== -1) {
                const opportunityKey = Object.keys(
                  defaultVariablesApi.opportunities_variables[indexKey]
                )[0];
                const base =
                  defaultVariablesApi.opportunities_variables[indexKey][
                    opportunityKey
                  ] || {};
                newValue = base[categoryKey]?.[informationKey] ?? '';
              } else {
                newValue = '';
              }
            } else if (defaultVariablesApi.offer_variables) {
              // Cas offre
              newValue =
                defaultVariablesApi.offer_variables[categoryKey]?.[
                  informationKey
                ] ?? '';
            } else {
              // Cas générique : variable simple dans defaultVariablesApi
              newValue =
                defaultVariablesApi[categoryKey]?.[informationKey] ?? '';
            }

            // Si on n'arrive pas à déterminer une valeur, on aligne sur ItemRefresher : ''
            if (typeof newValue === 'undefined' || newValue === null) {
              newValue = '';
            }

            // Mise à jour de la valeur de la variable
            variable.value = newValue;

            // Injection de la nouvelle valeur dans le document (texte, images, triggers)
            mutateInjectVariablesInDocument({
              variables: draft.variables,
              variable,
              variableKey: variableId,
              draft,
            });
          });
        }),
        false,
        'reloadAllApiVariables_itemRefresherLogic'
      );

      // 2) Mettre à jour tous les tableaux API (tables issues de getTables)
      const { boxes, fetchTable, updateBox, currentDocument: reloadDocument } = get();
      const apiTableBoxes = boxes.filter((b) => {
        if (!isATableBox(b.type) || !b.tableName) return false;
        // En offre, les tableaux injectés depuis les modèles d'opportunité
        // (modèle récursif / sections récursives) portent des données de niveau
        // opportunité. Aucun lien vers leur opportunité n'est persisté sur la box
        // (proposal absent), donc fetchTable retombe sur le document courant =
        // l'offre : l'endpoint offre ne sert pas ces tables → 404, et
        // fetchTableData vide alors la box. On ne les rafraîchit donc pas ici ;
        // elles conservent les données posées à l'injection (persistées au
        // chargement). En proposition opportunité, le document courant EST
        // l'opportunité : ces tableaux se rafraîchissent normalement.
        if (reloadDocument.type === 'offer' && b.fromSectionId != null) return false;
        return true;
      });

      if (apiTableBoxes.length > 0) {
        await Promise.all(
          apiTableBoxes.map((box) =>
            fetchTableData({
              fetchStoreFunc: fetchTable,
              updateBoxFunc: updateBox,
              box: {
                ...box,
              },
              split: box.content?.shouldSplit,
              refresh: true,
              // Geste explicite de rechargement des données : c'est ici qu'un
              // screenshot planning périmé doit être refait, puisque plus aucun
              // fetch d'édition ne le régénère (cf. fetchTableData).
              force: true,
            })
          )
        );
      }

      // 3) Recharger les variables récursives indexées
      const { loadedRecursiveSections, currentDocument } = get();
      if (loadedRecursiveSections.length > 0) {
        // Regrouper par modèle : dans une offre, plusieurs opportunités partagent
        // un même modèle (une entrée de loadedRecursiveSections par opportunité).
        // Un seul sync append=false par modèle, alimenté par les lignes de toutes
        // ses opportunités concaténées dans l'ordre d'injection, reproduit la
        // disposition posée à l'injection. Auparavant chaque entrée lançait son
        // propre sync append=false : elles s'effaçaient l'une l'autre (et en
        // Promise.all, de façon concurrente).
        const entriesByModel = new Map();
        for (const entry of loadedRecursiveSections) {
          if (!entriesByModel.has(entry.modelName)) entriesByModel.set(entry.modelName, []);
          entriesByModel.get(entry.modelName).push(entry);
        }
        // Séquentiel, pas de Promise.all : les syncs partagent le store.
        for (const [modelName, entries] of entriesByModel) {
          const allRows = [];
          for (const entry of entries) {
            const rows = await getRecursiveSectionData(entry.opportunityId, modelName, {
              // documentType STOCKÉ à l'injection : une section d'offre est
              // injectée par opportunité, donc 'opportunity'. Utiliser
              // currentDocument.type ('offer') tapait l'endpoint offre avec un id
              // d'opportunité → 404 → la reload ne rafraîchissait rien.
              documentType: entry.documentType ?? currentDocument.type,
              agreementType: entry.agreementType,
              sectionWork: entry.sectionWork,
            });
            if (rows?.length) allRows.push(...rows);
          }
          if (allRows.length) {
            await get().syncIndexedRecursiveVarsToStore(modelName, allRows, null, false, entries[0].agreementType);
          }
        }
      }

      message.success(
        "Les variables ont été rechargées avec les dernières données de l'API."
      );
    } catch (error) {
      console.error(error);
      message.error(
        "Une erreur est survenue lors du rechargement des variables depuis l'API."
      );
    } finally {
      get().setLoaderPdfToImage(false);
    }
  },
  reloadRecursiveSectionByModel: async (modelName) => {
    const meta = get().loadedRecursiveSections.find((m) => m.modelName === modelName);
    if (!meta) return;
    const rows = await getRecursiveSectionData(meta.opportunityId, modelName, {
      documentType: get().currentDocument.type,
      agreementType: meta.agreementType,
      sectionWork: meta.sectionWork,
    });
    if (rows?.length) await get().syncIndexedRecursiveVarsToStore(modelName, rows, null, false, meta.agreementType);
  },
  // Reçu sur le broadcast WS planning_screenshots_ready (worker terminé). Les images
  // récursives sont bakées dans box.content.src (≠ tableaux réactifs) ET le sync de
  // variables saute les images : on swappe donc directement le src des box image
  // planning, ciblées par recursiveRowId (= id du poste, posé à l'injection). Re-fetch
  // en LECTURE SEULE (force=false) -> pas de ré-enqueue -> pas de boucle.
  refreshRecursivePlanningImages: async () => {
    const PLANNING_FIELDS = RECURSIVE_WORK_POSITION_PLANNING_IMAGE_FIELDS;
    const meta = get().loadedRecursiveSections.find((m) => m.modelName === 'work_position');
    if (!meta) return;
    const rows = await getRecursiveSectionData(meta.opportunityId, 'work_position', {
      documentType: get().currentDocument.type,
      agreementType: meta.agreementType,
      sectionWork: meta.sectionWork,
      force: false,
    });
    if (!rows?.length) return;

    const rowsById = {};
    rows.forEach((r) => { rowsById[String(r._id)] = r; });

    set(
      produce((draft) => {
        draft.boxes.forEach((box) => {
          const adminValue = box.content?.adminValue;
          const rowId = box.content?.recursiveRowId;
          if (!adminValue || rowId == null) return;
          const parts = adminValue.split('.');
          if (parts.length !== 2 || parts[0] !== 'work_position' || !PLANNING_FIELDS.includes(parts[1])) return;
          const row = rowsById[String(rowId)];
          if (!row) return;
          // Valeur back : URL (image) | "" (pas d'image, poste sans planning -> pas de
          // loader) | sentinel PENDING (encore en cours). On reflète l'état courant.
          box.content = { ...box.content, src: String(row[parts[1]] ?? '') };
        });
      }),
      false,
      'refreshRecursivePlanningImages'
    );
  },
  // Bouton "Mettre à jour" d'UNE image planning récursive (box ciblée).
  // Même flux que l'injection : loader PENDING optimiste -> re-fetch force=true
  // (le back relance le microservice) -> on reflète l'état renvoyé (PENDING si
  // capture en cours, "" si poste sans planning -> pas de loader). L'URL fraîche
  // arrive via le broadcast WS (refreshRecursivePlanningImages). Renvoie true si
  // la box était bien une image planning récursive (sinon le caller garde son flux).
  refreshRecursivePlanningImage: async (boxId) => {
    const PLANNING_FIELDS = RECURSIVE_WORK_POSITION_PLANNING_IMAGE_FIELDS;
    const box = get().boxes.find((b) => b.id === boxId);
    const adminValue = box?.content?.adminValue;
    const rowId = box?.content?.recursiveRowId;
    if (!adminValue || rowId == null) return false;
    const parts = adminValue.split('.');
    if (parts.length !== 2 || parts[0] !== 'work_position' || !PLANNING_FIELDS.includes(parts[1])) return false;

    const meta = get().loadedRecursiveSections.find((m) => m.modelName === 'work_position');
    if (!meta) return false;

    // Loader immédiat (le src DB ne doit pas rester affiché pendant la regen).
    set(
      produce((draft) => {
        const target = draft.boxes.find((b) => b.id === boxId);
        if (target) target.content = { ...target.content, src: PENDING_SCREENSHOT };
      }),
      false,
      'refreshRecursivePlanningImage:pending'
    );

    const rows = await getRecursiveSectionData(meta.opportunityId, 'work_position', {
      documentType: get().currentDocument.type,
      agreementType: meta.agreementType,
      sectionWork: meta.sectionWork,
      force: true,
    });
    const row = rows?.find((r) => String(r._id) === String(rowId));
    set(
      produce((draft) => {
        const target = draft.boxes.find((b) => b.id === boxId);
        if (target) target.content = { ...target.content, src: String(row?.[parts[1]] ?? '') };
      }),
      false,
      'refreshRecursivePlanningImage'
    );
    return true;
  },
  applyTemplate: async (id) => {
    // Vider les résultats de recherche lors du changement de template
    get().clearSearchResults();
    // Un document rouvert repart d'un contenu neuf : le `res` gardé en cache est
    // muté sur place par le produce ci-dessous (une valeur assignée à un draft
    // n'est pas draftée), puis gelé par immer. Le rejouer dupliquait les sections
    // injectées, ou plantait sur un tableau gelé (masks). Le cache est fait pour
    // durer un document (cf. _templateCache), pas pour survivre au suivant.
    get().clearTemplateCache();

    set(
      produce((state) => {
        state.isDocumentLoading = true;
      }),
      false,
      `isDocumentLoading true`
    );
    const res = await get().getTemplate(id);
    const { documentType, id: docId } = get().currentDocument;
    await get().fetchTables(documentType, docId);
    await get().fetchDefaultVariablesApi();
    if (res.error) {
      emptyUrl();
      set(
        produce((state) => {
          state.currentProposal = { id: null };
          state.currentDocument = getDocumentFromParams();
          state.isDocumentLoading = false;
        }),
        false,
        `Template error : ${res.error.message}...`
      );
      return false;
    }
    res.content.configuration.fontFamily =
      res.content.configuration.fontFamily ?? defaultConfiguration.fontFamily;
    get().loadFont(res.content.configuration.fontFamily);
    // Doublons de styles laissés par les injections de section d'avant le
    // dédoublonnage : retirés à l'ouverture, puis enregistrés d'un bloc en fin
    // de chargement (la collab enverrait la configuration seule, sans les box
    // rebranchées).
    const dedupedContent = dedupeSectionStyles(res.content);
    const sectionStylesDeduped = dedupedContent !== res.content;
    res.content = dedupedContent;
    const sections = await getTemplateSections(id);
    res.content.triggers = mutateRemoveDeletedSectionTriggers({
      triggers: res.content.triggers,
      sections,
    });

    //Inject section if there are some in template
    let sectionBoxes;
    // Sections liées récursives à déplier après le chargement du layout : comme
    // dans applyProposal, leur contenu est un gabarit à répéter par ligne du
    // modèle, ce que seul injectRecursiveSection sait faire (et qui a besoin du
    // store, donc pas pendant le produce ci-dessous). Sans ce détour, la section
    // était injectée telle quelle, en un exemplaire, variables non substituées.
    const recursiveSectionLinkInjections = [];
    if (res.content.boxes.some((box) => box.type === ItemTypes.SECTION_LINK)) {
      // Exclude baked SECTION_LINK boxes (fromBoxId set = created by mutateInjectSection).
      // Only original SECTION_LINKs (placed directly in the template) need injection.
      sectionBoxes = res.content.boxes
        .filter((box) => box.type === ItemTypes.SECTION_LINK && !box.fromBoxId)
        .map((box) => ({ ...box }));
      await Promise.all(
        sectionBoxes.map(async (box) => {
          if (!sections.some((section) => section.id === box.sectionId))
            return box;
          const section = await get().getTemplate(box.sectionId);
          if (!section || section.error) return box;
          const { isRecursive, recursiveModelName } = getSectionRecursiveMeta(section);
          // Même aiguillage que dans applyProposal : hors mode modèle, une section
          // récursive se déplie ligne par ligne, jamais par injection directe.
          if (isRecursive && recursiveModelName && get().currentDocument.type !== 'template') {
            // Sauf si le contenu chargé la porte déjà dépliée (garde anti-doublon).
            if (!isRecursiveSectionExpandedAtAnchor(res.content.boxes, box.id, box.sectionId)) {
              recursiveSectionLinkInjections.push({
                boxId: box.id,
                section: { ...section, isRecursive, recursiveModelName },
              });
            }
            return box;
          }
          // Mode modèle (ou section récursive sans modèle, donc mal formée) : pas
          // d'expansion possible, on injecte le gabarit tel quel — sauf s'il est
          // déjà là.
          if (
            isRecursive &&
            isRecursiveSectionExpandedAtAnchor(res.content.boxes, box.id, box.sectionId)
          ) {
            return box;
          }
          const currentDocument = get().currentDocument;
          const sectionContent =
            currentDocument.type === 'template'
              ? section.content
              : {
                  ...section.content,
                  ...(await fillEmptyAdminTablesBoxes({
                    boxes: section.content.boxes,
                    containers: section.content.containers,
                    columns: section.content.columns,
                    proposable_id: currentDocument.id,
                    type: currentDocument.type,
                    landscape: get().landscape,
                    marginRight:
                      section.content.configuration.margins.right * gridUnit,
                    marginLeft:
                      section.content.configuration.margins.left * gridUnit,
                    header: section.content.masks.find(
                      ({ id }) => id === MASKS_TYPE.HEADER.id
                    ),
                    footer: section.content.masks.find(
                      ({ id }) => id === MASKS_TYPE.FOOTER.id
                    ),
                    gridConfiguration: section.content.gridConfiguration,
                    configuration: section.content.configuration,
                    triggers: section.content.triggers,
                    variableOrderArray: section.content.variableOrderArray,
                  })),
                };

          // Injection de la section dans le box
          box.sectionContent = sectionContent;
        })
      );
    }

    const validSectionIds = new Set(sections.map((section) => section.id));
    set(
      produce((draft) => {
        mutateApplyTemplate(draft, res);
        // Injection of linked sections in state
        if (sectionBoxes) {
          sectionBoxes.forEach((box) => {
            if (!sections.some((section) => section.id === box.sectionId)) {
              mutateRemmoveSectionContainers(draft, box.id, {
                sectionId: box.sectionId,
                dateTimestamp: draft.containers.find((container) =>
                  container.columnsIds.includes(box.columnId)
                ).dateTimestamp,
              });
            } else if (box.sectionContent) {
              // Cf. applyProposal : n'injecter que si le contenu a été récupéré
              // en phase 1, sinon on laisse le SECTION_LINK nu (pas de box vide).
              mutateInjectSection({
                draft,
                sectionId: box.sectionId,
                boxId: box.id,
                sectionContent: box.sectionContent,
                linked_section: true,
                validSectionIds,
              });
            }
            box.sectionContent = null;
          });
        }
      }),
      false,
      'applyTemplateLayout'
    );
    set(
      produce((draft) => {
        mutateSetGridConfiguration(draft);
      }),
      false,
      'setGridConfiguration'
    );
    // Dépliage des sections liées récursives, avant la fin du chargement : les
    // lignes du modèle sont récupérées puis le gabarit est injecté une fois par
    // ligne (injectRecursiveSection). Les relations doivent être en place, elles
    // servent à valider les enfants récursifs — l'appel est idempotent, celui
    // qui suit plus bas ne refetch rien.
    if (recursiveSectionLinkInjections.length > 0) {
      await get().fetchRecursiveSectionRelationships();
      for (const { boxId, section } of recursiveSectionLinkInjections) {
        await get().injectRecursiveSection({ section, boxId, split: false });
      }
    }
    // isDocumentLoading ne retombe qu'APRÈS toute la queue ci-dessous (finally).
    // Ce drapeau est la garde anti-perte de données de structureSync (cf.
    // collab/localEditGate.js) : tant qu'il est vrai, aucun diff n'est diffusé ni
    // persisté. Le baisser avant ces synchros ouvrait une fenêtre pendant laquelle
    // une bascule de document — setCurrentDocument vide toutes les collections —
    // était diffée comme « supprime tout » et écrite en base sur le document
    // PRÉCÉDENT (la cible est résolue à l'envoi, pas au calcul du diff). C'est ce
    // qui a vidé des modèles de section récursive de leurs boxes, containers,
    // colonnes et variables.
    // finally : une erreur dans la queue ne doit pas figer l'éditeur en
    // chargement perpétuel — l'ancien code baissait le drapeau avant, donc un
    // throw ici le laissait à false ; il faut garder cette propriété.
    try {
      // Load relationships once so the store is ready for recursive section validation
      await get().fetchRecursiveSectionRelationships();
      const templateModelName = get().currentTemplate?.recursiveModelName;
      // Category drives the materiel/materiel_security field schema. Embedded sections inherit
      // the document's agreement type here (no per-section category is known at this point);
      // it only matters for `materiel`, every other model has a single schema.
      const templateAgreementType = get().currentTemplate?.recursiveAgreementType;
      if (templateModelName) {
        await get().fetchRecursiveSectionFields(templateModelName, templateAgreementType);
        get().syncRecursiveVariablesToStore(templateModelName, null, templateAgreementType);
      }
      // Load fields for any embedded recursive sections already in the store
      const { variables: currentVars, recursiveModelFields: loadedFields } = get();
      const embeddedModels = new Set();
      Object.entries(currentVars).forEach(([key, variable]) => {
        if (!variable.isRecursive) return;
        const modelName = key.split('.')[0];
        if (modelName !== templateModelName && !loadedFields[modelName]) {
          embeddedModels.add(modelName);
        }
      });
      for (const modelName of embeddedModels) {
        await get().fetchRecursiveSectionFields(modelName, templateAgreementType);
        get().syncRecursiveVariablesToStore(modelName, null, templateAgreementType);
      }
      // Expose opportunity.* variables in the palette when editing an opportunity template
      const loadedTemplate = get().currentTemplate;
      if (
        loadedTemplate?.templatable === 'opportunity' &&
        loadedTemplate?.pattern === 'template' &&
        !loadedFields['opportunity']
      ) {
        await get().fetchRecursiveSectionFields('opportunity');
        get().syncRecursiveVariablesToStore('opportunity');
      }
    } finally {
      set(
        produce((state) => {
          state.isDocumentLoading = false;
        }),
        false,
        `isDocumentLoading false`
      );
    }
    if (sectionStylesDeduped && get().user?.isAdmin) {
      try {
        await get().updateTemplate();
      } catch (error) {
        console.error('Enregistrement du modèle nettoyé impossible', error);
      }
    }
    return true;
  },
  applyTemplateAndSaveProposal: async (templateId, proposalId) => {
    // Vider les résultats de recherche lors du changement de template/proposition
    get().clearSearchResults();

    const res = await get().getTemplate(templateId);
    if (!res) return false;
    if (res.error) {
      emptyUrl();
      set(
        produce((state) => {
          state.currentProposal = { id: proposalId };
          state.currentDocument = getDocumentFromParams();
        }),
        false,
        `Template error : ${res.error.message}...`
      );
      return false;
    }
    res.content.configuration.fontFamily =
      res.content.configuration.fontFamily ?? defaultConfiguration.fontFamily;
    get().loadFont(res.content.configuration.fontFamily);

    let _recursiveRows = null;
    const _recursiveModelName = res.content.isRecursive ? res.content.recursiveModelName : null;
    if (_recursiveModelName) {
      const opportunityId = get().currentDocument.id;
      if (opportunityId) {
        const rows = await getRecursiveSectionData(opportunityId, _recursiveModelName, {
          documentType: get().currentDocument.type,
          agreementType: res.content.recursiveAgreementType,
        });
        if (rows && rows.length > 0) {
          res.content = expandContentForRows(res.content, rows, _recursiveModelName);
          _recursiveRows = rows;
        }
      }
    }

    set(
      produce((draft) => {
        mutateApplyTemplate(draft, res);
      }),
      false,
      'applyTemplateLayout'
    );
    set(
      produce((draft) => {
        mutateSetGridConfiguration(draft);
      }),
      false,
      'setGridConfiguration'
    );
    // Expanse les sections liées avant de sauvegarder : le proposal créé n'a
    // jamais de SECTION_LINK brut en DB (pas de ré-injection/divergence au load).
    // On réinjecte l'expansion dans le store pour que l'éditeur l'affiche aussi.
    {
      const cur = get();
      if (cur.currentDocument.id) {
        // Doublons de styles du modèle : le document naît sans eux.
        const expandedContent = await get().expandLinkedSectionsAtCreation({
          content: {
            boxes: cur.boxes,
            containers: cur.containers,
            columns: cur.columns,
            masks: cur.masks,
            variables: cur.variables,
            variableGroups: cur.variableGroups,
            variableOrderArray: cur.variableOrderArray,
            triggers: cur.triggers,
            configuration: cur.configuration,
            gridConfiguration: cur.gridConfiguration,
            summaryConfiguration: cur.summaryConfiguration,
          },
          templateId,
          documentType: cur.currentDocument.type,
          documentId: cur.currentDocument.id,
          landscape: cur.landscape,
        });
        const expanded = dedupeSectionStyles(expandedContent);
        set(
          produce((draft) => {
            draft.boxes = expanded.boxes;
            draft.containers = expanded.containers;
            draft.columns = expanded.columns;
            draft.masks = expanded.masks;
            draft.variables = expanded.variables;
            draft.variableGroups = expanded.variableGroups;
            draft.variableOrderArray = expanded.variableOrderArray;
            draft.triggers = expanded.triggers;
            draft.configuration = expanded.configuration;
          }),
          false,
          'expandLinkedSectionsAtCreation'
        );
      }
    }
    if (_recursiveModelName && _recursiveRows) {
      await get().syncIndexedRecursiveVarsToStore(
        _recursiveModelName,
        _recursiveRows,
        null,
        false,
        res.content.recursiveAgreementType
      );
    }

    const {
      landscape,
      boxes,
      pages,
      masks,
      configuration,
      summaryConfiguration,
      gridConfiguration,
      variables,
      currentDocument,
      currentProposal,
      containers,
      columns,
      rowContainers,
      defaultVariablesApi,
      variableGroups,
      variableOrderArray,
      triggers,
    } = get();
    if (!currentDocument.id) return;

    const proposalData = await proposalsApi.postProposal({
      landscape,
      currentDocument,
      currentProposal,
      boxes,
      pages,
      masks,
      configuration,
      summaryConfiguration,
      gridConfiguration,
      containers,
      columns,
      rowContainers,
      variables,
      variableGroups,
      variableOrderArray,
      triggers,
    });
    proposalData.content = formatResContent(
      proposalData.content,
      defaultVariablesApi,
      false,
      landscape
    );
    const { id } = proposalData;
    set(
      produce((state) => {
        state.currentProposal = { id };
        Object.keys(proposalData.content).forEach((k) => {
          state[k] = proposalData.content[k];
        });
      }),
      false,
      'saveProposal'
    );
    return true;
  },

  createTemplateInStore: (templatable) => {
    set(
      produce((state) => {
        state.masks = defaultMasks;
        state.currentTemplate = null;
        state.currentProposal = null;
        state.configuration = defaultConfiguration;
        state.summaryConfiguration =
          defaultItemValues.summary.content.configuration;
        state.gridConfiguration = defaultItemValues;
        state.boxes = [];
        state.variables = {};
        state.tempaltable = templatable;
        state.containers = [];
        state.columns = [];
        state.variableGroups = [];
        state.copies = [];
        state.variableOrderArray = [];
        state.triggers = {};
        state.sectionsToActivate = [];
        state.hasOpportunityOption = false;
        state.hasCostingActivated = false;
        state.hasOpportunityFA = false;
        state.hasCompanyWorkFA = false;
        state.zoneNames = [];
        state.zoneIcon = 'fa-kit fa-solid-bars-progress-gear';
        state.itemsChangedByTriggers = [];
        state.tmp = undefined;
      }),
      false,
      'createTemplateInStore'
    );
  },
});

// Construit le sectionContent injectable pour une section liée, identique à
// applyProposal / handleSectionAction : sur un proposal, remplit les tables
// admin vides via fillEmptyAdminTablesBoxes ; sur un template, renvoie le
// contenu brut. Renvoie null si la section n'existe plus / erreur de fetch.
const buildSectionContentForInjection = async ({
  getTemplate,
  sectionId,
  currentDocument,
  landscape,
}) => {
  const section = await getTemplate(sectionId);
  if (!section || section.error) return null;
  if (currentDocument.type === 'template') return section.content;
  return {
    ...section.content,
    ...(await fillEmptyAdminTablesBoxes({
      boxes: section.content.boxes,
      containers: section.content.containers,
      columns: section.content.columns,
      proposable_id: currentDocument.id,
      type: currentDocument.type,
      landscape,
      marginRight: section.content.configuration.margins.right * gridUnit,
      marginLeft: section.content.configuration.margins.left * gridUnit,
      header: section.content.masks.find(
        ({ id }) => id === MASKS_TYPE.HEADER.id
      ),
      footer: section.content.masks.find(
        ({ id }) => id === MASKS_TYPE.FOOTER.id
      ),
      gridConfiguration: section.content.gridConfiguration,
      configuration: section.content.configuration,
      triggers: section.content.triggers,
      variableOrderArray: section.content.variableOrderArray,
    })),
  };
};

const mutateApplyTemplate = (draft, data) => {
  draft.copies = [];
  draft.landscape = data.landscape;
  draft.hasCostingActivated = data.has_costing_activated;
  draft.hasCompanyWorkFA = !!data.has_company_work_fa;
  draft.zoneIcon = data.zone_icon ?? draft.zoneIcon;
  Object.keys(data.content).forEach((k) => {
    draft[k] = data.content[k];
  });
  draft.currentTemplate = {
    name: data.name,
    id: data.id,
    templatable: data.templatable || 'opportunity',
    pattern: data.pattern,
    landscape: data.landscape,
    work: data.work,
    preview: data.preview,
    document_type_ids: data.document_type_ids,
    isRecursive: data.content?.isRecursive ?? false,
    recursiveModelName: data.content?.recursiveModelName ?? null,
    recursiveAgreementType: data.content?.recursiveAgreementType ?? null,
  };
  draft.configuration = withUniqueColors(
    syncStructure(data.content.configuration, defaultConfiguration)
  );
  draft.summaryConfiguration = syncStructure(
    data.content.summaryConfiguration,
    defaultItemValues.summary.content.configuration
  );
  draft.gridConfiguration = syncStructure(
    data.content.gridConfiguration,
    defaultItemValues
  );
  // draft.pages = syncPageStructure(data.content.pages);
  if (draft.hasOwnProperty('pages')) {
    delete draft.pages;
  }
  // mutateBoxesPageIndex(
  //   draft.boxes,
  //   draft.pages.map((p) => p.id)
  // );
  mutateBoxesOrderInfinite(draft);
  mutateTitlesIndexes(draft);
  mutateResizeSummary(draft);
  // Réordonner variableOrderArray selon l'ordre visuel réel des boxes
  sortVariableOrderArray(draft);
};

const mutateSetGridConfiguration = (draft) => {
  const { margins, indentationTitles } = draft.configuration;
  draft.gridConfiguration[ItemTypes.SUMMARY] = {
    ...draft.gridConfiguration[ItemTypes.SUMMARY],
    width: calculateWidth(margins, indentationTitles, 0),
    left: calculateLeftPadding(margins, indentationTitles, 0),
  };
  draft.gridConfiguration[ItemTypes.TITLE] = {
    ...draft.gridConfiguration[ItemTypes.TITLE],
    width: calculateWidth(margins, indentationTitles, 0),
    left: calculateLeftPadding(margins, indentationTitles, 0),
  };
  draft.gridConfiguration[ItemTypes.TITLE_2] = {
    ...draft.gridConfiguration[ItemTypes.TITLE_2],
    width: calculateWidth(margins, indentationTitles, 1),
    left: calculateLeftPadding(margins, indentationTitles, 1),
  };
  draft.gridConfiguration[ItemTypes.TITLE_3] = {
    ...draft.gridConfiguration[ItemTypes.TITLE_3],
    width: calculateWidth(margins, indentationTitles, 2),
    left: calculateLeftPadding(margins, indentationTitles, 2),
  };
  draft.gridConfiguration[ItemTypes.TITLE_4] = {
    ...draft.gridConfiguration[ItemTypes.TITLE_4],
    width: calculateWidth(margins, indentationTitles, 3),
    left: calculateLeftPadding(margins, indentationTitles, 3),
  };
  draft.gridConfiguration[ItemTypes.TABLE] = {
    ...draft.gridConfiguration[ItemTypes.TABLE],
    width: calculateWidth(margins, indentationTitles, 0),
    left: calculateLeftPadding(margins, indentationTitles, 0),
  };
};
